import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewChild, Output, NgModule } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import * as Three from 'three';
import { Mesh, Line, Sprite, ArrowHelper, DirectionalLight, Scene, Color, AmbientLight, AxesHelper, GridHelper, OrthographicCamera, Vector3, Group, RingGeometry, CylinderGeometry, SphereGeometry, BoxGeometry, MeshBasicMaterial, DoubleSide, FrontSide, Quaternion, EdgesGeometry, LineSegments, LineBasicMaterial, BufferGeometry } from 'three';
import { Evaluator, Brush, SUBTRACTION } from 'three-bvh-csg';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import * as ThreeMeshUI from 'three-mesh-ui';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@angular/material/progress-spinner';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

class ProboscisViewerService {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

function recursivelyFindObjects(children, filterFn) {
    const objects = [];
    const matchingObjects = children.filter(c => filterFn(c));
    objects.push(...matchingObjects);
    // find matching items in children as well
    children.forEach(child => {
        const matchingChilds = recursivelyFindObjects(child.children, filterFn);
        if (matchingChilds.length > 0)
            objects.push(...matchingChilds);
    });
    return objects;
}
function findObjectsByFilterFunction(group, filterFn) {
    const objects = recursivelyFindObjects(group.children, filterFn);
    return objects;
}
function disposeAndClearGroup(group) {
    group.traverse(obj => {
        if (obj instanceof Mesh || obj instanceof Line || obj instanceof Sprite) {
            obj.geometry.dispose();
            obj.material.dispose();
        }
        if (obj instanceof ArrowHelper) {
            obj.dispose();
        }
    });
    group.clear();
}

var ObjectType;
(function (ObjectType) {
    ObjectType[ObjectType["Mesh"] = 0] = "Mesh";
    ObjectType[ObjectType["Control"] = 1] = "Control";
})(ObjectType || (ObjectType = {}));
//Function to add lights to the scene
function addDirectionalLight(scene, x, y, z) {
    const color = 0xffffff;
    const intensity = 1.0 / 2.0;
    const light = new DirectionalLight(color, intensity);
    light.position.set(x, y, z);
    scene.add(light);
}
function createScene() {
    const scene = new Scene();
    scene.background = new Color(0xffffff);
    return scene;
}
function addDefaultLights(scene) {
    const ambientLight = new AmbientLight(0xffffbb, 0.5);
    scene.add(ambientLight);
    addDirectionalLight(scene, 0, -8, 0);
}
function createAxisHelper(scene) {
    const axesHelper = new AxesHelper(5);
    axesHelper.renderOrder = 30;
    scene.add(axesHelper);
}
function createGrid(group, addXGrid = true) {
    disposeAndClearGroup(group);
    const size = 20;
    const divisions = 20;
    if (addXGrid) {
        let gridHelperX = new GridHelper(size, divisions);
        gridHelperX.renderOrder = 10;
        gridHelperX.rotateX(1.5708);
        group.add(gridHelperX);
    }
    let gridHelperZ = new GridHelper(size, divisions);
    gridHelperZ.renderOrder = 20;
    group.add(gridHelperZ);
}
function setCameraInitialValues(camera) {
    if (camera == null)
        return;
    if (camera instanceof OrthographicCamera) {
        camera.zoom = 1;
    }
    camera.rotation.x = 0;
    camera.rotation.y = 0;
    camera.rotation.z = 0;
    camera.position.x = 0;
    camera.position.y = -8;
    camera.position.z = 0;
    camera.up = new Vector3(0, 0, 1);
}

var CalculationStatus;
(function (CalculationStatus) {
    CalculationStatus["Success"] = "Success";
    CalculationStatus["NoActiveLoadAttachment"] = "NoActiveLoadAttachment";
    CalculationStatus["SingularPosition"] = "SingularPosition";
    CalculationStatus["LackingLiftCapacity"] = "LackingLiftCapacity";
})(CalculationStatus || (CalculationStatus = {}));
var Unit;
(function (Unit) {
    Unit["Length"] = "Length";
    Unit["Angle"] = "Angle";
})(Unit || (Unit = {}));
var BearingType;
(function (BearingType) {
    BearingType["Standard"] = "Standard";
    BearingType["Virtual"] = "Virtual";
    BearingType["PostProcessing"] = "PostProcessing";
    BearingType["SlewingAxisAxial"] = "SlewingAxisAxial";
    BearingType["SlewingAxisRadial"] = "SlewingAxisRadial";
})(BearingType || (BearingType = {}));
var PointType;
(function (PointType) {
    PointType["Masspoint"] = "masspoint";
    PointType["ComponentMasspoint"] = "componentmasspoint";
    PointType["RopePathPoint"] = "ropepathpoint";
})(PointType || (PointType = {}));

//Returns random float from min-max interval provided
function getRandomAxesLength(min, max) {
    return Math.random() * (max - min) + min;
}
//Builds all the cartesians coordinate systems
function buildCartesiansInternal(parent, cartesian, color) {
    const quaternionAxis = createQuaternion(cartesian);
    const groupArrow = new Group();
    const xDir = new Vector3(1, 0, 0);
    const yDir = new Vector3(0, 1, 0);
    const zDir = new Vector3(0, 0, 1);
    //normalize the direction vector (convert to vector of length 1)
    xDir.normalize();
    yDir.normalize();
    zDir.normalize();
    const origin = new Vector3(0, 0, 0);
    //random between 0.7-1.3
    const length = getRandomAxesLength(0.07, 0.13);
    const arrowX = new ArrowHelper(xDir, origin, length, color, 0.004, 0.02);
    const arrowY = new ArrowHelper(yDir, origin, length, color, 0.001, 0.005);
    const arrowZ = new ArrowHelper(zDir, origin, length, color, 0.001, 0.005);
    groupArrow.add(arrowX);
    groupArrow.add(arrowY);
    groupArrow.add(arrowZ);
    positionObject(groupArrow, cartesian.position.x, cartesian.position.y, cartesian.position.z);
    groupArrow.setRotationFromQuaternion(quaternionAxis);
    parent.add(groupArrow);
}
//Builds graphics for a masspoint or rope path point
function buildPointGraphic(type, parent, pointData, index) {
    const pointGeometry = {
        type: type,
    };
    const material = materialFactory(pointGeometry);
    const point = geometriesFactory(pointGeometry);
    const pointGroup = new Group();
    const meshPoint = new Mesh(point, material);
    meshPoint.renderOrder = getRenderOrder(pointGeometry) + index + 5;
    pointGroup.name = pointData.name;
    positionObject(meshPoint, pointData.position.x, pointData.position.y, pointData.position.z);
    pointGroup.add(meshPoint);
    parent.add(pointGroup);
}
//Position object with translate method
function positionObject(object, x, y, z) {
    object.translateX(x);
    object.translateY(y);
    object.translateZ(z);
}
//Rotates and places mesh locally
function rotateAndTranslateGeometry(geometry, geometryItem) {
    if (geometryItem.translate) {
        geometry.translateX(geometryItem.translate.x);
        geometry.translateY(geometryItem.translate.y);
        geometry.translateZ(geometryItem.translate.z);
    }
    if (geometryItem.rotate) {
        geometry.rotateX(geometryItem.rotate.x);
        geometry.rotateY(geometryItem.rotate.y);
        geometry.rotateZ(geometryItem.rotate.z);
    }
}
//Created correct geometry depending on .type in Json
function geometriesFactory(geometryItem) {
    switch (geometryItem.type.toLowerCase()) {
        case "box":
            return new BoxGeometry(geometryItem.width, geometryItem.height, geometryItem.depth);
        case "cylinder":
            return new CylinderGeometry(geometryItem.diameter / 2, geometryItem.diameter / 2, geometryItem.height, 8);
        case "sphere":
            return new SphereGeometry(geometryItem.diameter / 2, 9, 6);
        case "ropepathpoint":
        case "masspoint":
        case "componentmasspoint":
            return new SphereGeometry(0.02, 9, 6);
        case "bearing":
            if (geometryItem.bearingType === BearingType.SlewingAxisAxial) {
                return new RingGeometry((geometryItem.diameter - geometryItem.width) / 2, (geometryItem.diameter + geometryItem.width) / 2, 8);
            }
            return new CylinderGeometry(geometryItem.diameter / 2, geometryItem.diameter / 2, geometryItem.width, 8);
        default:
            console.log("Geometry type not supported for building Geometry: " + geometryItem.type);
            break;
    }
}
function getColor(geometryItem) {
    switch (geometryItem.type.toLowerCase()) {
        case "box":
            return 0x000000;
        case "cylinder":
            if (geometryItem.subType.toLowerCase() == "axle") {
                return 0x00FF00;
            }
            return 0x0000FF;
        case "cartesian":
            return 0xf22307;
        case "sphere":
            return 0xf705db;
        case "ropepathpoint":
            return 0xff00ff;
        case "masspoint":
            return 0x000000;
        case "bearing":
            switch (geometryItem.bearingType) {
                case BearingType.Standard:
                    return 0xCD7F32;
                case BearingType.Virtual:
                    return 0xA020F0;
                case BearingType.PostProcessing:
                    return 0xFFFF00;
                case BearingType.SlewingAxisAxial:
                    return 0x00CC99;
                case BearingType.SlewingAxisRadial:
                    return 0x34AEEB;
                default:
                    return 0xCD7F32;
            }
        case "componentmasspoint":
            return 0xFF0000;
        default:
            return 0xf22307;
    }
}
//Created correct material depending on .type in Json
function materialFactory(geometryItem) {
    const color = getColor(geometryItem);
    switch (geometryItem.type.toLowerCase()) {
        case "box":
        case "cylinder":
        case "cartesian":
        case "sphere":
            return new MeshBasicMaterial({
                color: color,
                opacity: 0.2,
                transparent: true,
                depthTest: false,
                depthWrite: true,
            });
        case "ropepathpoint":
        case "masspoint":
        case "componentmasspoint":
            return new MeshBasicMaterial({
                color: color,
                opacity: 0.6,
                transparent: true,
                depthTest: false,
                depthWrite: true,
            });
        case "bearing":
            return new MeshBasicMaterial({
                color: color,
                opacity: 0.4,
                transparent: true,
                depthTest: false,
                depthWrite: true,
                side: geometryItem.bearingType === BearingType.SlewingAxisAxial ? DoubleSide : FrontSide,
            });
        default:
            console.log("Geometry type not supported for Material: " + geometryItem.type);
            return undefined;
    }
}
function getRenderOrder(geometryItem) {
    switch (geometryItem.type.toLowerCase()) {
        case "box":
            return 100;
        case "cylinder":
            return 200;
        case "cartersian":
            return 300;
        case "sphere":
            return 400;
        case "masspoint":
            return 500;
        case "bearing":
            return 600;
        case "componentmasspoint":
            return 700;
        default:
            return 0;
    }
}
function createQuaternion(geometryItem) {
    return new Quaternion(geometryItem.quaternion.qi, geometryItem.quaternion.qj, geometryItem.quaternion.qk, geometryItem.quaternion.qr);
}
//Builds a geometry from data provided
function buildGeometries(parent, geometryItem, index, isAxles) {
    try {
        const isAxle = geometryItem?.subType?.toLowerCase() === "axle";
        if (isAxles !== isAxle) {
            return;
        }
        const quaternion = createQuaternion(geometryItem);
        let geometry = geometriesFactory(geometryItem);
        const group = new Group();
        positionObject(group, geometryItem.position.x, geometryItem.position.y, geometryItem.position.z);
        // Radial bearings in slewing axis connections has to be rotated 90 degrees
        if (geometryItem.bearingType === BearingType.SlewingAxisRadial) {
            geometryItem['rotate'] = { x: Math.PI / 2, y: 0, z: 0 };
        }
        const material = materialFactory(geometryItem);
        if (geometryItem.forks?.length > 0) {
            geometry = addForksToGeometry(geometry, geometryItem, quaternion);
        }
        let geometryMesh = new Mesh(geometry, material);
        geometryMesh.renderOrder = getRenderOrder(geometryItem) + index;
        geometryMesh.name = geometryItem.name;
        geometryMesh.userData = { type: ObjectType.Mesh, driveControlName: geometryItem.driveControl };
        let geometryEdges = new EdgesGeometry(geometry);
        let geometryLine = new LineSegments(geometryEdges, new LineBasicMaterial({ color: 0xf1111 }));
        geometryLine.renderOrder = getRenderOrder(geometryItem) + index + 5;
        group.add(geometryMesh);
        group.add(geometryLine);
        if (!geometryItem.forks?.length) {
            rotateAndTranslateGeometry(geometryMesh, geometryItem);
            rotateAndTranslateGeometry(geometryLine, geometryItem);
            group.setRotationFromQuaternion(quaternion);
        }
        else {
            positionObject(geometryMesh, -geometryItem.position.x, -geometryItem.position.y, -geometryItem.position.z);
            positionObject(geometryLine, -geometryItem.position.x, -geometryItem.position.y, -geometryItem.position.z);
        }
        parent.add(group);
    }
    catch (error) {
        console.log("Could not build geometry. " + error);
    }
}
function buildHoistRopeLine(group, hoistRope, ropePathPoints) {
    if (hoistRope === undefined || !ropePathPoints)
        return;
    const pointsInOrder = hoistRope?.ropePathPointIdxs?.map((idx) => ropePathPoints[idx]);
    const pointPositions = pointsInOrder?.map((point) => new Vector3(point.position.x, point.position.y, point.position.z));
    if (!pointPositions?.length)
        return;
    const geometry = new BufferGeometry().setFromPoints(pointPositions);
    const material = materialFactory({ type: PointType.RopePathPoint });
    const line = new Line(geometry, material);
    group.add(line);
}
function buildPointsGraphicForMachine(type, group, points) {
    if (points === undefined)
        return;
    let index = 1;
    points.forEach((point) => {
        buildPointGraphic(type, group, point, index);
        index++;
    });
}
function buildCartesians(group, jsonData, showSubCartesians) {
    disposeAndClearGroup(group);
    jsonData.components.forEach((geoItem) => {
        if (geoItem.subCartesians && showSubCartesians) {
            geoItem.subCartesians.forEach((cartesian) => {
                const color = getColor(geoItem);
                buildCartesiansInternal(group, cartesian, color);
            });
        }
        else if (geoItem.cartesians && !showSubCartesians) {
            geoItem.cartesians.forEach((cartesian) => {
                const color = getColor(geoItem);
                buildCartesiansInternal(group, cartesian, color);
            });
        }
    });
}
function addForksToGeometry(geometry, geometryItem, quaternion) {
    if (!geometryItem.forks?.length) {
        return geometry;
    }
    const evaluator = new Evaluator();
    const forkSide = Math.max(geometryItem.height, geometryItem.width, geometryItem.depth) * 2 + 0.2; // Guarantees that the fork will always subtract that whole geometry regardless of angle
    const forkGeometry = new BoxGeometry(forkSide, 0.1, forkSide);
    let geometryBrush = new Brush(geometry);
    positionObject(geometryBrush, geometryItem.position.x, geometryItem.position.y, geometryItem.position.z);
    geometryBrush.applyQuaternion(quaternion);
    rotateAndTranslateGeometry(geometryBrush, geometryItem);
    geometryBrush.updateMatrixWorld();
    for (let fork of geometryItem.forks) {
        let forkQuaternion = createQuaternion(fork);
        const forkBrush = new Brush(forkGeometry);
        positionObject(forkBrush, fork.position.x, fork.position.y, fork.position.z);
        forkBrush.applyQuaternion(forkQuaternion);
        forkBrush.translateX(forkSide / 2);
        forkBrush.updateMatrixWorld();
        geometryBrush = evaluator.evaluate(geometryBrush, forkBrush, SUBTRACTION);
    }
    return geometryBrush.geometry;
}

function rotateContainer(container, position, moveLength) {
    container.position.x = position.x + moveLength.x;
    container.position.y = position.y + moveLength.y;
    container.position.z = position.z + moveLength.z;
    container.userData['originalPositionX'] = container.position.x;
    container.userData['originalPositionY'] = container.position.y;
    container.userData['originalPositionZ'] = container.position.z;
}
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function buildHook(hook) {
    const widthMultiplier = 0.12;
    let highestNumberLength = returnHighestNumberStringLength(hook);
    const dynamicWidth = highestNumberLength * widthMultiplier;
    const container = new ThreeMeshUI.Block({
        justifyContent: 'center',
        offset: 0.02,
        margin: 0.02,
        borderRadius: 0.03,
        backgroundOpacity: 40,
        backgroundColor: getBackgroundColor(hook),
        width: dynamicWidth,
        height: 0.4,
    });
    container.name = 'hookContainer';
    const grossLoadContainer = new ThreeMeshUI.Block({
        justifyContent: 'center',
        fontFamily: './assets/Roboto-msdf.json',
        fontTexture: './assets/Roboto-msdf.png',
        fontSize: 0.07,
        padding: 0.01,
        backgroundOpacity: 0,
        backgroundColor: new Three.Color(0xffffff),
        width: 0.55,
        height: 0.1,
    });
    const grossLoadText = new ThreeMeshUI.Text({
        content: getGrossLoadTextContent(hook),
        fontSize: 0.1,
        fontColor: new Three.Color(0xffffff),
        BestFit: 'grow',
        whiteSpace: 'pre'
    });
    grossLoadText.name = 'grossLoadText';
    grossLoadContainer.add(grossLoadText);
    container.add(grossLoadContainer);
    const positionXContainer = generateHookPositionContainer('hookPositionX', 'X:', printPosition(hook.position.x), dynamicWidth);
    const positionYContainer = generateHookPositionContainer('hookPositionY', 'Y:', printPosition(hook.position.y), dynamicWidth);
    const positionZContainer = generateHookPositionContainer('hookPositionZ', 'Z:', printPosition(hook.position.z), dynamicWidth);
    container.add(positionXContainer, positionYContainer, positionZContainer);
    // position container
    rotateContainer(container, hook.position, new Three.Vector3(1.5, 0, -1));
    container.renderOrder = Infinity;
    return container;
}
function updateHook(container, hook) {
    rotateContainer(container, hook.position, new Three.Vector3(1.5, 0, -1));
    container.set({ backgroundColor: getBackgroundColor(hook) });
    let grossLoadText = container.getObjectByName('grossLoadText');
    if (grossLoadText)
        grossLoadText.set({ content: getGrossLoadTextContent(hook) });
    let positionXText = container.getObjectByName('hookPositionX');
    if (positionXText)
        positionXText.set({ content: printPosition(hook.position.x) });
    let positionYText = container.getObjectByName('hookPositionY');
    if (positionYText)
        positionYText.set({ content: printPosition(hook.position.y) });
    let positionZText = container.getObjectByName('hookPositionZ');
    if (positionZText)
        positionZText.set({ content: printPosition(hook.position.z) });
}
function getBackgroundColor(hook) {
    // If hook utilization is above 100%, show a red box
    return new Three.Color(!!hook.utilization && hook.utilization >= 1 ? 0xFF0000 : 0x333333);
}
function getGrossLoadTextContent(hook) {
    return hook?.calculationStatus == CalculationStatus.Success && hook?.grossLoad !== undefined ? `${hook.grossLoad.toFixed(0)} kg` : 'No load';
}
function generateHookPositionContainer(positionTextName, coordinateHeaderText, coordinateText, dynamicWidth) {
    const positionContainerOptions = {
        contentDirection: 'row',
        justifyContent: 'space-between',
        fontFamily: './assets/Roboto-msdf.json',
        fontTexture: './assets/Roboto-msdf.png',
        fontSize: 0.07,
        padding: 0.01,
        backgroundOpacity: 0,
        backgroundColor: new Three.Color(0xffffff),
        width: 0.55,
        height: 0.1,
    };
    const positionBlockOptions = {
        justifyContent: 'center',
        fontFamily: './assets/Roboto-msdf.json',
        fontTexture: './assets/Roboto-msdf.png',
        fontSize: 0.07,
        backgroundOpacity: 0,
        backgroundColor: new Three.Color(0xffffff),
        width: 0.1,
        height: 0.1,
    };
    const positionTextOptions = {
        fontSize: 0.075,
        fontColor: new Three.Color(0xffffff),
        BestFit: 'grow',
    };
    // position x
    const positionContainer = new ThreeMeshUI.Block(positionContainerOptions);
    const positionHeaderBlock = new ThreeMeshUI.Block(positionBlockOptions);
    const positionHeaderText = new ThreeMeshUI.Text({
        ...positionTextOptions,
        content: coordinateHeaderText,
    });
    positionHeaderBlock.add(positionHeaderText);
    positionContainer.add(positionHeaderBlock);
    const positionBlock = new ThreeMeshUI.Block({
        ...positionBlockOptions,
        width: dynamicWidth * 0.7,
        textAlign: 'right',
        padding: 0.02,
    });
    const positionText = new ThreeMeshUI.Text({
        ...positionTextOptions,
        content: coordinateText,
    });
    positionText.name = positionTextName;
    positionBlock.add(positionText);
    positionContainer.add(positionBlock);
    return positionContainer;
}
function printPosition(coordinate) {
    return (coordinate * 1000).toFixed(0) + " mm";
}
function returnHighestNumberStringLength(hook) {
    const { x, y, z } = hook.position;
    const maxLength = Math.max(x, y, z) * 1000;
    const length = maxLength.toFixed(0).length;
    return Math.max(length, 5);
}

var MathOperations;
(function (MathOperations) {
    MathOperations["Addition"] = "Addition";
    MathOperations["Subtraction"] = "Subtraction";
})(MathOperations || (MathOperations = {}));
class MachineControls {
    constructor(renderer, camera, driveGroup, virtualGroup, controlPositionsEmitter, utilizationGroup, geometryGroup, hookGroup, cartesianOriginalPos) {
        this.renderer = renderer;
        this.camera = camera;
        this.driveGroup = driveGroup;
        this.virtualGroup = virtualGroup;
        this.controlPositionsEmitter = controlPositionsEmitter;
        this.utilizationGroup = utilizationGroup;
        this.geometryGroup = geometryGroup;
        this.hookGroup = hookGroup;
        this.cartesianOriginalPos = cartesianOriginalPos;
        this.selectState = false;
        this.shiftKey = false;
        this.mouse = new Three.Vector2(undefined, undefined);
        this.raycaster = new Three.Raycaster();
        this.driveObjsToTest = [];
        this.virtualObjsToTest = [];
        this.currentToggleGroup = ControlType.None;
        this.activeControl = null;
        this.isHoldDown = false;
        this.setupEventListeners();
    }
    setupEventListeners() {
        window.addEventListener('pointermove', (event) => {
            const canvas = this.renderer.domElement;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            this.mouse.x = (x / (rect.right - rect.left)) * 2 - 1;
            this.mouse.y = (y / (rect.bottom - rect.top)) * -2 + 1;
        });
        window.addEventListener('pointerdown', (event) => {
            this.selectState = true;
            this.isHoldDown = true;
            this.shiftKey = event.shiftKey;
        });
        window.addEventListener('pointerup', () => {
            this.selectState = false;
            this.isHoldDown = false;
        });
        window.addEventListener('touchstart', (event) => {
            this.selectState = true;
            this.mouse.x = (event.touches[0].clientX / window.innerWidth) * 2 - 1;
            this.mouse.y = -(event.touches[0].clientY / window.innerHeight) * 2 + 1;
        });
        window.addEventListener('touchend', () => {
            this.selectState = false;
            this.mouse.x = 0;
            this.mouse.y = 0;
        });
        window.addEventListener('keydown', (event) => {
            if (this.activeControl != null) {
                if (event.altKey || event.shiftKey) {
                    switch (event.key) {
                        case '+':
                            if (!this.isHoldDown) {
                                this.keyControlTrigger(event, MathOperations.Addition);
                                this.interval = setInterval(() => {
                                    this.keyControlTrigger(event, MathOperations.Addition);
                                }, 100);
                                this.isHoldDown = true;
                            }
                            break;
                        case '-':
                            if (!this.isHoldDown) {
                                this.keyControlTrigger(event, MathOperations.Subtraction);
                                this.interval = setInterval(() => {
                                    this.keyControlTrigger(event, MathOperations.Subtraction);
                                }, 100);
                                this.isHoldDown = true;
                            }
                            break;
                        default:
                    }
                }
            }
        });
        window.addEventListener('keyup', (event) => {
            this.isHoldDown = false;
            clearInterval(this.interval);
        });
    }
    generateControlValueText(control) {
        let value = Math.round(control.value).toString();
        if (control.unit == ControlUnit.Angle) {
            value += "°";
        }
        else if (control.unit == ControlUnit.Length) {
            value += " mm";
        }
        return value;
    }
    updateActiveControl() {
        if (this.activeControl != null) {
            this.jsonData.controls.forEach((control) => {
                if (control.name == this.activeControl.name) {
                    this.activeControl.value = control.value;
                }
            });
        }
    }
    keyControlTrigger(event, mathOperation) {
        if (this.activeControl != null) {
            if (this.activeControl?.unit == ControlUnit.Angle) {
                if (mathOperation == MathOperations.Addition) {
                    this.activeControl.value += event.shiftKey ? 10 : 1;
                }
                else {
                    this.activeControl.value -= event.shiftKey ? 10 : 1;
                }
            }
            else {
                if (mathOperation == MathOperations.Addition) {
                    this.activeControl.value += event.shiftKey ? 1000 : 100;
                }
                else {
                    this.activeControl.value -= event.shiftKey ? 1000 : 100;
                }
            }
            var index = 0;
            this.jsonData.controls.forEach((control) => {
                if (control.name == this.activeControl.name) {
                    this.jsonData.controls[index].value = this.activeControl.value;
                    this.emitControlValues();
                }
                index++;
            });
        }
        else {
            clearInterval(this.interval);
        }
    }
    scaleObject(object, vector = undefined) {
        if (vector == undefined) {
            vector = object.position;
        }
        let scale = 1;
        const controlsDistanceToCamera = 2.6, maxScale = 6, minScale = 0.8;
        if (this.camera instanceof Three.OrthographicCamera) {
            scale = controlsDistanceToCamera / this.camera.zoom;
        }
        if (this.camera instanceof Three.PerspectiveCamera) {
            scale = vector.distanceTo(this.camera.position) / controlsDistanceToCamera;
        }
        scale = Math.min(maxScale, Math.max(minScale, scale));
        if (object.scale.x !== scale || object.scale.y !== scale || object.scale.z !== scale) {
            object.scale.set(scale, scale, scale);
        }
    }
    emitControlValues(isControlsPositioningMethod = true) {
        if (isControlsPositioningMethod && !this.jsonData || !this.jsonData.controls)
            return;
        let controlValuesJson = {
            PositioningMethod: isControlsPositioningMethod ? "Controls" : "LoadAttachment",
            ActiveControlSet: this.currentToggleGroup,
            ControlValues: new Array(),
            LoadAttachmentValues: new Array()
        };
        if (isControlsPositioningMethod) {
            this.jsonData.controls
                .filter((control) => control.type == this.currentToggleGroup)
                .forEach((control) => {
                controlValuesJson['ControlValues'].push({
                    "name": control.name,
                    "value": control.value
                });
            });
        }
        const multiplyThousand = (n) => { return Math.round(n * 1000); };
        // att load attachment values as well
        if (this.jsonData.hook && !isControlsPositioningMethod) {
            controlValuesJson['LoadAttachmentValues'].push({
                name: this.jsonData.hook.name,
                value: [multiplyThousand(this.jsonData.hook.position.x), multiplyThousand(this.jsonData.hook.position.y), multiplyThousand(this.jsonData.hook.position.z)]
            });
        }
        this.controlPositionsEmitter.emit(controlValuesJson);
    }
    setCurrentToggleGroup(group) {
        this.currentToggleGroup = group;
    }
    setControls(jsonData) {
        const oldControls = this.jsonData?.controls ? JSON.parse(JSON.stringify(this.jsonData.controls)) : undefined;
        this.jsonData = jsonData;
        if (this.hasControlsChanged(oldControls) || (!this.driveGroup.children.length && !this.virtualGroup.children.length)) {
            this.buildControls();
        }
        else {
            this.updateControls();
        }
    }
    setCamera(camera) {
        this.camera = camera;
    }
    hasControlsChanged(oldControlData) {
        if (!oldControlData && !this.jsonData.controls) {
            return false;
        }
        if (oldControlData?.length !== this.jsonData.controls?.length) {
            return true;
        }
        for (const [index, oldControl] of oldControlData.entries()) {
            let currentControl = this.jsonData.controls[index];
            if (oldControl.name !== currentControl.name ||
                oldControl.state !== currentControl.state ||
                oldControl.limit.min !== currentControl.limit.min ||
                oldControl.limit.max !== currentControl.limit.max) {
                return true;
            }
        }
        return false;
    }
    buildContainer() {
        const container = new ThreeMeshUI.Block({
            width: 1,
            height: 1,
            justifyContent: 'center',
            fontFamily: './assets/Roboto-msdf.json',
            fontTexture: './assets/Roboto-msdf.png',
            fontSize: 0.07,
            padding: 0.01,
            backgroundOpacity: 0,
            backgroundColor: new Three.Color(0xffffff)
        });
        //@ts-ignore
        container.getBackgroundMaterial().depthTest = false;
        return container;
    }
    scaleAndPositionContainer(container, control, moveHorizontal, moveVertical) {
        // Set scale
        const offsetVector = control.offsetPosition
            ? new Vector3(control.offsetPosition.x, control.offsetPosition.y, control.offsetPosition.z)
            : undefined;
        this.scaleObject(container, offsetVector);
        // Reset rotation
        container.rotation.x = 0;
        container.rotation.y = 0;
        container.rotation.z = 0;
        // Set position
        container.position.x = control.offsetPosition?.x ?? control.position.x;
        container.position.y = control.offsetPosition?.y ?? control.position.y;
        container.position.z = control.offsetPosition?.z ?? control.position.z;
        container.userData['originalPositionX'] = container.position.x + moveHorizontal;
        container.userData['originalPositionY'] = container.position.y;
        container.userData['originalPositionZ'] = container.position.z + moveVertical;
    }
    buildControls() {
        disposeAndClearGroup(this.driveGroup);
        disposeAndClearGroup(this.virtualGroup);
        disposeAndClearGroup(this.utilizationGroup);
        disposeAndClearGroup(this.hookGroup);
        disposeAndClearGroup(this.cartesianOriginalPos);
        this.disposeBlockObjArray(this.driveObjsToTest);
        this.driveObjsToTest = [];
        this.disposeBlockObjArray(this.virtualObjsToTest);
        this.virtualObjsToTest = [];
        let driveDistance = -0.5;
        this.updateActiveControl();
        if (!this.jsonData) {
            return;
        }
        if (!this.jsonData.controls) {
            throw new Error('No json data to build controls with');
        }
        this.jsonData.controls.forEach((control) => {
            this.addOriginalCartesianOfControl(control);
            const headerBlock = this.buildControlHeader(control);
            const controlBlock = this.buildControlButtons(control);
            const container = this.buildContainer();
            container.name = control.name + "Container";
            controlBlock.name = control.name + "Control";
            container.add(headerBlock, controlBlock);
            if (control.type == ControlType.Drive) {
                if (control.state !== null && control.state !== undefined) {
                    this.buildCylinderUtilizationBlocks(control, container, driveDistance);
                    driveDistance = driveDistance + 0.25;
                }
                this.scaleAndPositionContainer(container, control, driveDistance, 0.7);
                this.driveGroup.add(container);
                this.buildLine(control.name, control.type, false);
            }
            else {
                this.scaleAndPositionContainer(container, control, -1, 1.3);
                this.virtualGroup.add(container);
                this.buildLine(control.name, control.type, false);
            }
        });
        if (this.jsonData.hook) {
            const hookContainer = buildHook(this.jsonData.hook);
            this.scaleObject(hookContainer);
            this.hookGroup.add(hookContainer);
            // build line between hook container and hook position
            const points = [];
            points.push(new Three.Vector3(this.jsonData.hook.position.x, this.jsonData.hook.position.y, this.jsonData.hook.position.z));
            points.push(hookContainer.position);
            const lineGeometry = new Three.BufferGeometry().setFromPoints(points);
            const lineMaterial = new Three.LineBasicMaterial({ color: 0x000000 });
            const line = new Three.Line(lineGeometry, lineMaterial);
            line.name = 'hookLine';
            this.hookGroup.add(line);
        }
        if (this.jsonData.activeHoist) {
            this.buildHoist();
        }
        ThreeMeshUI.update();
    }
    buildCylinderUtilizationBlocks(control, driveControlContainer, driveDistance) {
        // Build utilization block for virtual control mode
        const virtualModeUtilizationContainer = this.buildContainer();
        const virtualModeUtilization = this.buildUtilizationBlock(control, true);
        virtualModeUtilization.name = control.name + "Control";
        this.virtualObjsToTest.push(virtualModeUtilization);
        virtualModeUtilizationContainer.add(this.buildControlHeader(control), virtualModeUtilization);
        this.scaleAndPositionContainer(virtualModeUtilizationContainer, control, driveDistance, 0);
        this.utilizationGroup.add(virtualModeUtilizationContainer);
        this.buildLine(control.name, control.type, true);
        // Build utilization block for drive control mode
        const driveModeUtilization = this.buildUtilizationBlock(control, false);
        driveModeUtilization.name = `${control.name}Utilization`;
        driveControlContainer.add(driveModeUtilization);
    }
    findActiveHoistComponent() {
        return this.jsonData.components.find((x) => x.name === this.jsonData.activeHoist.name);
    }
    buildHoist() {
        const hoist = this.findActiveHoistComponent();
        if (!hoist) {
            return;
        }
        this.addOriginalCartesianOfControl(hoist);
        const hoistContainer = this.buildContainer();
        const hoistInfo = this.buildUtilizationBlock({ ...this.jsonData.activeHoist, type: hoist.type }, true);
        hoistInfo.name = hoist.name + "Control";
        this.virtualObjsToTest.push(hoistInfo);
        hoistContainer.add(this.buildControlHeader(hoist), hoistInfo);
        this.scaleAndPositionContainer(hoistContainer, hoist, -0.2, -0.4);
        this.utilizationGroup.add(hoistContainer);
        this.buildLine(hoist.name, '', true);
        hoistContainer.add(hoistInfo);
    }
    updateControls() {
        if (!this.jsonData?.controls) {
            return;
        }
        let driveDistance = -0.5;
        this.updateActiveControl();
        this.jsonData.controls.forEach((control) => {
            const controlContainerName = `${control.name}Container`;
            if (control.type == ControlType.Drive) {
                if (control.state !== null && control.state !== undefined) {
                    this.updateUtilizationContainer(control);
                    driveDistance = driveDistance + 0.25;
                    const cylinderInfoContainer = this.utilizationGroup.getObjectByName(`${control.name}Control`)?.parent;
                    if (cylinderInfoContainer) {
                        this.scaleAndPositionContainer(cylinderInfoContainer, control, driveDistance, 0);
                    }
                }
                let container = this.driveGroup.getObjectByName(controlContainerName);
                if (container !== undefined) {
                    this.scaleAndPositionContainer(container, control, driveDistance, 0.7);
                }
            }
            else {
                let container = this.virtualGroup.getObjectByName(controlContainerName);
                if (container !== undefined) {
                    this.scaleAndPositionContainer(container, control, -1, 1.3);
                }
            }
            let controlText = control.type === ControlType.Drive
                ? this.driveGroup.getObjectByName(`${control.name}ControlText`)
                : this.virtualGroup.getObjectByName(`${control.name}ControlText`);
            if (controlText !== undefined) {
                controlText.set({ content: this.generateControlValueText(control) });
            }
            // Update position of point
            const originalPos = this.cartesianOriginalPos.getObjectByName(`${control.name}Original`);
            if (originalPos !== undefined) {
                originalPos.position.x = control.offsetPosition.x;
                originalPos.position.y = control.offsetPosition.y;
                originalPos.position.z = control.offsetPosition.z;
            }
        });
        if (this.jsonData.hook) {
            let hookContainer = this.hookGroup.getObjectByName('hookContainer');
            if (!hookContainer) {
                throw new Error('Unable to find hookContainer');
            }
            this.scaleObject(hookContainer);
            updateHook(hookContainer, this.jsonData.hook);
            // build line between hook container and hook position
            const points = [];
            points.push(new Three.Vector3(this.jsonData.hook.position.x, this.jsonData.hook.position.y, this.jsonData.hook.position.z));
            points.push(hookContainer.position);
            let hookLine = this.hookGroup.getObjectByName('hookLine');
            if (hookLine && hookLine instanceof Three.Line) {
                hookLine.geometry.setFromPoints(points);
            }
        }
        if (this.jsonData.activeHoist) {
            this.updateHoist();
        }
        ThreeMeshUI.update();
    }
    updateHoist() {
        const hoist = this.findActiveHoistComponent();
        if (!hoist) {
            return;
        }
        this.updateUtilizationContainer(this.jsonData.activeHoist);
        const hoistContainer = this.utilizationGroup.getObjectByName(`${hoist.name}Control`)?.parent;
        if (hoistContainer) {
            this.scaleAndPositionContainer(hoistContainer, hoist, -0.2, -0.4);
        }
        const originalPos = this.cartesianOriginalPos.getObjectByName(`${this.jsonData.activeHoist.name}Original`);
        if (originalPos !== undefined) {
            originalPos.position.x = hoist.position.x;
            originalPos.position.y = hoist.position.y;
            originalPos.position.z = hoist.position.z;
        }
    }
    updateUtilizationContainer(control) {
        const container = this.currentToggleGroup === ControlType.Drive
            ? this.driveGroup.getObjectByName(`${control.name}Utilization`)
            : this.utilizationGroup.getObjectByName(`${control.name}Control`);
        if (!container) {
            return;
        }
        let text = container?.childrenTexts[0];
        if (control?.utilization !== undefined && control?.utilization > 0.99) {
            container.set({ backgroundColor: new Three.Color(0xFF0000) });
            this.changeCylinderColor(control.name);
        }
        else {
            container.set({ backgroundColor: new Three.Color(0x0000FF) });
        }
        text.set({ content: this.getUtilizationTextContent(control) });
        ThreeMeshUI.update();
    }
    getUtilizationTextContent(control) {
        const utilization = control.utilization ?? 0;
        return `${(utilization * 100).toFixed(0)}% ${this.getStateSymbol(control)}`;
    }
    buildControlHeader(control) {
        const container = new ThreeMeshUI.Block({
            width: 1.0,
            height: 0.1,
            justifyContent: 'center',
            offset: 0.02,
            margin: 0.02,
            backgroundOpacity: 0,
            backgroundColor: new Three.Color(0xffffff),
        });
        const controlText = new ThreeMeshUI.Text({
            content: control.name,
            fontSize: 0.075,
            fontColor: new Three.Color(0x000000),
            BestFit: 'grow'
        });
        container.name = control.name + "Header" + control.type;
        container.visible = false;
        container.add(controlText);
        return container;
    }
    changeCylinderColor(name) {
        const controlCylinders = findObjectsByFilterFunction(this.geometryGroup, (object) => {
            const match = object.userData['driveControlName'] == name;
            return match;
        });
        controlCylinders.forEach(cylinder => {
            // @ts-ignore
            cylinder.material.color = new Three.Color(0xFF0000);
        });
    }
    getStateSymbol(control) {
        if (!control.state) {
            return '';
        }
        if (control?.movingDirection == "Negative") {
            return ">" + control.state?.substring(0, 1) + "<";
        }
        else if (control?.movingDirection == "Positive") {
            return "<" + control.state?.substring(0, 1) + ">";
        }
        else {
            return "|" + control.state?.substring(0, 1) + "|";
        }
    }
    /**
     *
     * @param control object containing name, type and utilization
     * @param isSeparate flag defining if the block is separate or connected to another block (drive)
     */
    buildUtilizationBlock(control, isSeparate) {
        let backGroundColor = new Three.Color(0x0000FF);
        if (control.utilization > 0.99) {
            backGroundColor = new Three.Color(0xFF0000);
            this.changeCylinderColor(control.name);
        }
        const container = new ThreeMeshUI.Block({
            justifyContent: 'center',
            contentDirection: 'row-reverse',
            fontFamily: './assets/Roboto-msdf.json',
            fontTexture: './assets/Roboto-msdf.png',
            fontSize: 0.07,
            padding: 0.02,
            borderRadius: 0.05,
            width: 0.45,
            height: 0.13,
            backgroundColor: backGroundColor,
        });
        const lineName = isSeparate ? `${control.name}InfoLine` : `${control.name}ControlLine`;
        const hoveredStateAttributes = {
            state: 'hovered',
            onSet: () => {
                this.setControlVisibility(this.driveGroup.getObjectByName(`${control.name}Header${control.type}`), true);
                this.setControlVisibility(this.utilizationGroup.getObjectByName(`${control.name}Header${control.type}`), true);
                this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(`${control.name}Original`), true);
                this.updateLine(control.name, control.type, isSeparate);
                this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(lineName), true);
            },
        };
        const idleStateAttributes = {
            state: 'idle',
            onSet: () => {
                this.setControlVisibility(this.driveGroup.getObjectByName(`${control.name}Header${control.type}`), false);
                this.setControlVisibility(this.utilizationGroup.getObjectByName(`${control.name}Header${control.type}`), false);
                this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(`${control.name}Original`), false);
                this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(lineName), false);
            },
        };
        const text = new ThreeMeshUI.Text({
            content: this.getUtilizationTextContent(control),
            fontSize: 0.075,
            fontColor: new Three.Color(0xFFFFFF),
            BestFit: 'grow',
            padding: 0.1,
        });
        container.add(text);
        // @ts-ignore
        container.setupState(hoveredStateAttributes);
        // @ts-ignore
        container.setupState(idleStateAttributes);
        this.driveObjsToTest.push(container);
        return container;
    }
    buildControlButtons(control) {
        const container = new ThreeMeshUI.Block({
            justifyContent: 'center',
            contentDirection: 'row-reverse', // should probably be something else
            fontFamily: './assets/Roboto-msdf.json',
            fontTexture: './assets/Roboto-msdf.png',
            fontSize: 0.07,
            borderRadius: 0.05,
            width: 0.45,
            height: 0.13,
        });
        const buttonOptions = {
            width: 0.10,
            height: 0.10,
            justifyContent: 'center',
            offset: 0.02,
            margin: 0.02,
            borderRadius: 0.05
        };
        const textOptions = {
            width: 0.45,
            height: 0.13,
            justifyContent: 'center',
            margin: 0.02,
            borderRadius: 0.05,
            backgroundOpacity: 0,
        };
        const hoveredStateAttributes = {
            state: 'hovered',
            attributes: {
                backgroundColor: new Three.Color(0x999999),
                backgroundOpacity: 1,
                fontColor: new Three.Color(0xffffff),
            },
            onSet: () => {
                this.setControlGroupVisibility(control.name, control.type, true);
            },
        };
        const idleStateAttributes = {
            state: 'idle',
            attributes: {
                backgroundColor: new Three.Color(0x666666),
                backgroundOpacity: 0.3,
                fontColor: new Three.Color(0xffffff)
            },
            onSet: () => {
                this.setControlGroupVisibility(control.name, control.type, false);
            },
        };
        const selectedAttributes = {
            backgroundColor: new Three.Color(0x777777),
            fontColor: new Three.Color(0x222222)
        };
        const buttonPlus = new ThreeMeshUI.Block(buttonOptions);
        const buttonMinus = new ThreeMeshUI.Block(buttonOptions);
        const buttonText = new ThreeMeshUI.Text({
            content: this.generateControlValueText(control),
            fontSize: 0.075,
            BestFit: 'grow'
        });
        buttonText.name = `${control.name}ControlText`;
        const textBlock = new ThreeMeshUI.Block(textOptions).add(buttonText);
        // Add text to buttons
        buttonPlus.add(new ThreeMeshUI.Text({ content: '+' }));
        buttonPlus.name = control.name + "Plus" + control.type;
        buttonMinus.add(new ThreeMeshUI.Text({ content: '-' }));
        buttonMinus.name = control.name + "Minus" + control.type;
        // setupState comes via a mixin which doesn't seem to get detected via typescript types.d.ts :(
        // @ts-ignore
        buttonPlus.setupState({
            state: 'selected',
            attributes: selectedAttributes,
            onSet: async () => {
                while (this.isHoldDown) {
                    let activeControl = this.jsonData.controls.find(x => x.name === control.name);
                    if (this.currentToggleGroup === ControlType.None || !activeControl)
                        return;
                    if (activeControl.unit === Unit.Angle)
                        activeControl.value += this.shiftKey ? 10 : 1;
                    else
                        activeControl.value += this.shiftKey ? 1000 : 100;
                    // @ts-ignore
                    buttonText.set({ content: this.generateControlValueText(activeControl) });
                    this.emitControlValues();
                    await sleep(50);
                }
            }
        });
        // @ts-ignore
        buttonPlus.setupState(hoveredStateAttributes);
        // @ts-ignore
        buttonPlus.setupState(idleStateAttributes);
        // @ts-ignore
        buttonMinus.setupState({
            state: 'selected',
            attributes: selectedAttributes,
            onSet: async () => {
                while (this.isHoldDown) {
                    let activeControl = this.jsonData.controls.find(x => x.name === control.name);
                    if (this.currentToggleGroup === 'None' || !activeControl)
                        return;
                    if (activeControl.unit === Unit.Angle)
                        activeControl.value -= this.shiftKey ? 10 : 1;
                    else
                        activeControl.value -= this.shiftKey ? 1000 : 100;
                    // @ts-ignore
                    buttonText.set({ content: this.generateControlValueText(activeControl) });
                    this.emitControlValues();
                    await sleep(50);
                }
            }
        });
        let textPointerDownListener = (event) => {
            // trigger an Enter event
            textListener(new KeyboardEvent('keyup', {
                key: 'Enter'
            }));
            return true;
        };
        let textListener = (event) => {
            let validkeys = ['-', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'Backspace', 'Enter', 'Escape', '.'];
            if (validkeys.indexOf(event.key) < 0)
                return false;
            let buttonContent = buttonText.userData['content'];
            let activeControl = this.jsonData.controls.find(x => x.name === control.name);
            if (!activeControl)
                return false;
            switch (event.key) {
                // @ts-expect-error
                case 'Escape':
                    // restore earlier value
                    buttonText.userData['content'] = activeControl.value;
                // fallthrough to Enter case
                case 'Enter':
                    // update control value
                    if (buttonText.userData['content'] && buttonText.userData['content'].length > 0) {
                        activeControl.value = parseFloat(parseFloat(buttonText.userData['content']).toFixed(4));
                    }
                    // @ts-ignore
                    buttonText.set({ content: this.generateControlValueText(activeControl) });
                    window.removeEventListener('keyup', textListener);
                    window.removeEventListener('pointerdown', textPointerDownListener);
                    this.activeControl = null;
                    this.emitControlValues();
                    return true;
                case 'Backspace':
                    if (buttonContent.length > 0)
                        buttonContent = buttonContent.slice(0, -1);
                    break;
                case '-':
                    if (!event.altKey || !event.shiftKey) {
                        buttonContent += event.key;
                    }
                    break;
                default:
                    buttonContent += event.key;
            }
            buttonText.userData['content'] = buttonContent;
            // @ts-ignore
            buttonText.set({ content: buttonContent });
            return true;
        };
        // @ts-ignore
        textBlock.setupState({
            state: 'selected',
            attributes: selectedAttributes,
            onSet: () => {
                this.setControlGroupVisibility(control.name, control.type, true);
                let activeControl = this.jsonData.controls.find(x => x.name === control.name);
                if (!activeControl)
                    return;
                // @ts-ignore
                buttonText.set({ content: activeControl.value.toFixed(4) });
                buttonText.userData['content'] = '';
                window.addEventListener('keyup', textListener);
                window.addEventListener('pointerdown', textPointerDownListener);
                this.activeControl = activeControl;
            }
        });
        // @ts-ignore
        textBlock.setupState(idleStateAttributes);
        // @ts-ignore
        textBlock.setupState(hoveredStateAttributes);
        // @ts-ignore
        buttonMinus.setupState(hoveredStateAttributes);
        // @ts-ignore
        buttonMinus.setupState(idleStateAttributes);
        container.add(buttonPlus, textBlock, buttonMinus);
        // @ts-ignore
        container.setupState(hoveredStateAttributes);
        // @ts-ignore
        container.setupState(idleStateAttributes);
        if (control.type == 'Virtual') //
            this.virtualObjsToTest.push(buttonPlus, buttonMinus, textBlock);
        else
            this.driveObjsToTest.push(buttonPlus, buttonMinus, textBlock);
        return container;
    }
    setControlGroupVisibility(controlName, controlType, visible) {
        const controlGroup = controlType === ControlType.Drive ? this.driveGroup : this.virtualGroup;
        this.setControlVisibility(controlGroup.getObjectByName(`${controlName}Minus${controlType}`), visible);
        this.setControlVisibility(controlGroup.getObjectByName(`${controlName}Plus${controlType}`), visible);
        this.setControlVisibility(controlGroup.getObjectByName(`${controlName}Header${controlType}`), visible);
        this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(`${controlName}Original`), visible);
        if (visible) {
            this.updateLine(controlName, controlType, false);
        }
        this.setControlVisibility(this.cartesianOriginalPos.getObjectByName(`${controlName}ControlLine`), visible);
    }
    setControlVisibility(control, visible) {
        if (control)
            control.visible = visible;
    }
    updateButtonStates() {
        if (this.mouse.x !== 0 && this.mouse.y !== 0) {
            this.raycaster.setFromCamera(this.mouse, this.camera);
            const intersect = this.raycast();
            // Update targeted button state (if any)
            // @ts-ignore
            if (intersect && intersect.object.isUI) {
                if (this.selectState) {
                    // Component.setState internally call component.set with the options you defined in component.setupState
                    // @ts-ignore
                    intersect.object.setState('selected');
                }
                else {
                    // Component.setState internally call component.set with the options you defined in component.setupState
                    // @ts-ignore
                    intersect.object.setState('hovered');
                }
            }
            else {
                this.selectState = false;
            }
            // Update non-targeted buttons state
            this.virtualObjsToTest.concat(this.driveObjsToTest).forEach((obj) => {
                // @ts-ignore
                if ((!intersect || obj !== intersect.object) && obj.isUI) {
                    // Component.setState internally call component.set with the options you defined in component.setupState
                    // @ts-ignore
                    obj.setState('idle');
                }
            });
            ThreeMeshUI.update();
        }
        let moveTowardsCameraDistance = 0.1;
        let children = this.driveGroup.children.concat(this.virtualGroup.children, this.utilizationGroup.children);
        if (this.hookGroup.children.length > 0)
            children = children.concat(this.hookGroup.children[0]);
        // update containers to face camera
        children.forEach(container => {
            // move container towards camera, max 0.1 per iteration to avoid jittering
            let originalPosition = new Vector3(container.userData['originalPositionX'], container.userData['originalPositionY'], container.userData['originalPositionZ']);
            originalPosition.lerp(this.camera.position, moveTowardsCameraDistance); // move to desired location
            // only move controls if distance from desired location gets to big, to avoid jittering, or if the user has stopped holding down the mouse button
            if (originalPosition.distanceTo(container.position) > 0.5 || !this.selectState) {
                container.position.fromArray(originalPosition.toArray());
                // scale with distance to camera
                this.scaleObject(container);
            }
            // have container face camera
            container.quaternion.copy(this.camera.quaternion);
        });
        // update hook line position
        if (this.hookGroup.children.length > 1) {
            const hookPoints = [];
            hookPoints.push(new Three.Vector3(this.jsonData.hook.position.x, this.jsonData.hook.position.y, this.jsonData.hook.position.z));
            hookPoints.push(new Three.Vector3(this.hookGroup.children[0].position.x, this.hookGroup.children[0].position.y, this.hookGroup.children[0].position.z));
            this.hookGroup.children[1].geometry.setFromPoints(hookPoints);
        }
    }
    raycast() {
        let closestIntersection = null;
        let objsToTest = this.currentToggleGroup === ControlType.Virtual ? this.virtualObjsToTest : this.driveObjsToTest;
        objsToTest.forEach((obj) => {
            const intersects = this.raycaster.intersectObject(obj, true);
            if (intersects == null || intersects.length == 0)
                return;
            intersects[0].object = obj;
            if (closestIntersection == null) {
                closestIntersection = intersects[0];
                return;
            }
            if (closestIntersection.distance > intersects[0].distance) {
                closestIntersection = intersects[0];
            }
        });
        return closestIntersection;
    }
    addOriginalCartesianOfControl(control) {
        const massPoint = new Three.SphereGeometry(0.03, 9, 6);
        const massPointGroup = new Three.Group();
        const material = new Three.MeshPhongMaterial({
            color: 0x000000,
            opacity: 1,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });
        const meshMassPoint = new Three.Mesh(massPoint, material);
        meshMassPoint.renderOrder = Infinity;
        massPointGroup.add(meshMassPoint);
        massPointGroup.translateX(control.offsetPosition?.x ?? control.position.x);
        massPointGroup.translateY(control.offsetPosition?.y ?? control.position.y);
        massPointGroup.translateZ(control.offsetPosition?.z ?? control.position.z);
        massPointGroup.name = control.name + "Original";
        massPointGroup.visible = false;
        this.cartesianOriginalPos.add(massPointGroup);
    }
    updateLine(controlName, controlType, isSeparate) {
        const lineName = isSeparate ? `${controlName}InfoLine` : `${controlName}ControlLine`;
        const line = this.cartesianOriginalPos.getObjectByName(lineName);
        if (!line) {
            return;
        }
        const points = this.buildPointsForLine(controlName, controlType, isSeparate);
        line.geometry.setFromPoints(points);
    }
    buildLine(controlName, controlType, isSeparate) {
        const material = new Three.LineBasicMaterial({
            color: 0x000000,
            opacity: 1,
            transparent: true,
            depthTest: false,
            depthWrite: false,
        });
        const points = this.buildPointsForLine(controlName, controlType, isSeparate);
        // @ts-ignore
        const geometry = new Three.BufferGeometry({ color: 0x000000, opacity: 1, transparent: true, depthTest: false, depthWrite: false }).setFromPoints(points);
        const line = new Three.Line(geometry, material);
        line.renderOrder = Infinity;
        line.name = isSeparate ? `${controlName}InfoLine` : `${controlName}ControlLine`;
        this.cartesianOriginalPos.add(line);
    }
    buildPointsForLine(controlName, controlType, isSeparate) {
        const originalPos = this.cartesianOriginalPos.getObjectByName(`${controlName}Original`)?.position;
        let activeGroup;
        if (isSeparate) {
            activeGroup = this.utilizationGroup;
        }
        else if (controlType === ControlType.Drive) {
            activeGroup = this.driveGroup;
        }
        else if (controlType === ControlType.Virtual) {
            activeGroup = this.virtualGroup;
        }
        else {
            throw new Error(`Unable to build line for control type: '${controlType}'`);
        }
        const controlPos = activeGroup.getObjectByName(`${controlName}Control`)?.parent?.position;
        return [originalPos, controlPos];
    }
    disposeBlockObjArray(objs) {
        objs.forEach(block => {
            block.traverse(obj => {
                if (obj instanceof Three.Mesh) {
                    obj.geometry.dispose();
                    obj.material.dispose();
                }
            });
        });
    }
}

class WorkingRegion {
    constructor(viewer, group, renderer, camera, controlPositionsEmitter) {
        this.viewer = viewer;
        this.group = group;
        this.renderer = renderer;
        this.camera = camera;
        this.controlPositionsEmitter = controlPositionsEmitter;
        this.coordinates = [];
        this.raycaster = new Three.Raycaster();
        // @ts-ignore
        this.material = new Three.MeshBasicMaterial({
            color: 0xcccccc,
            side: Three.DoubleSide,
        });
        // subscribe to pointerdown events
        window.addEventListener('pointerdown', (event) => {
            this.checkRaycast(event);
        });
    }
    getCoordinates() {
        return this.coordinates;
    }
    hasWorkingRegion() {
        return this.coordinates.length > 0;
    }
    setJsonData(jsonData) {
        this.jsonData = jsonData;
    }
    load(coordinates) {
        this.coordinates = coordinates;
        this.group.clear();
        if (this.coordinates.length == 0) {
            this.group.visible = false;
            this.viewer.showWorkingRegion = false;
            return false;
        }
        const shape = new Three.Shape();
        shape.moveTo((this.coordinates[0].x) / 1000, (this.coordinates[0].z) / 1000);
        for (let i = 1; i < this.coordinates.length; i++) {
            shape.lineTo((this.coordinates[i].x) / 1000, (this.coordinates[i].z) / 1000);
        }
        const geometry = new Three.ShapeGeometry(shape);
        const mesh = new Three.Mesh(geometry, this.material);
        mesh.translateY((this.coordinates[0].y) / 1000);
        mesh.rotation.x = Math.PI / 2;
        mesh.name = "workingRegion";
        this.group.add(mesh);
        return true;
    }
    checkRaycast(event) {
        if (!event.ctrlKey || this.group.children.length == 0)
            return; // we should only trigger if user is holding ctrl-key
        if (!this.jsonData) {
            console.warn("Positioning from working region requested but no jsonData has been provided to working region class, unable to position machine");
            return;
        }
        // get x and y coordinates
        const canvas = this.renderer.domElement;
        const rect = canvas.getBoundingClientRect();
        let x = event.clientX - rect.left;
        let y = event.clientY - rect.top;
        x = (x / (rect.right - rect.left)) * 2 - 1;
        y = (y / (rect.bottom - rect.top)) * -2 + 1;
        this.raycaster.setFromCamera(new Three.Vector2(x, y), this.viewer.getActiveCamera());
        const intersects = this.raycaster.intersectObjects(this.group.children, true);
        if (intersects && intersects.length > 0) {
            this.controlPositionsEmitter.emit({
                PositioningMethod: "OnDefaultWorkingRegion",
                ActiveControlSet: ControlType[this.viewer.getActiveControl()],
                ControlValues: new Array(),
                LoadAttachmentValues: [
                    {
                        name: this.jsonData.hook.name,
                        value: [intersects[0].point.x * 1000, intersects[0].point.y * 1000, intersects[0].point.z * 1000],
                    }
                ]
            });
        }
    }
}

class Cuts {
    constructor(viewer, group, renderer, camera) {
        this.viewer = viewer;
        this.group = group;
        this.renderer = renderer;
        this.lineMaterial = new Three.LineBasicMaterial({
            color: 0x0000ff
        });
        this.anchorMaterial = new Three.MeshBasicMaterial({
            color: 0x8888ff
        });
    }
    setJsonData(jsonData) {
        this.jsonData = jsonData;
        this.parseCuts();
    }
    parseCuts() {
        disposeAndClearGroup(this.group);
        this.jsonData.components.forEach(component => {
            if (!component.cuts)
                return;
            component.cuts.forEach(cut => this.parseCut(cut, component.name));
        });
    }
    parseCut(cut, componentName) {
        const cutGroup = new Three.Group();
        cutGroup.name = cut.name;
        cutGroup.userData['type'] = 'cut';
        cutGroup.userData['componentName'] = componentName;
        cut.cutGraphic?.arcs?.forEach(arc => {
            cutGroup.add(this.createArc(arc));
        });
        cut.cutGraphic?.lines?.forEach(line => {
            cutGroup.add(this.createLine(line));
        });
        // Create symbol for anchor point
        const anchorGroup = this.createAnchorPoint();
        cutGroup.add(anchorGroup);
        cutGroup.add(anchorGroup.children[0]); // Adding the box directly to cut group to enable raycast
        // Adding a transparent mesh to enable raycast intersection
        const box = new Three.Box3().setFromObject(cutGroup);
        const planeGeometry = new Three.PlaneGeometry(box.max.z - box.min.z, box.max.y - box.min.y);
        const transparentMaterial = new Three.MeshBasicMaterial({ opacity: 0, transparent: true, side: Three.DoubleSide });
        const mesh = new Three.Mesh(planeGeometry, transparentMaterial);
        cutGroup.add(mesh);
        const boxCenter = box.getCenter(new Three.Vector3());
        mesh.position.set(boxCenter.x, boxCenter.y, boxCenter.z);
        mesh.rotateY(Math.PI / 2);
        positionObject(cutGroup, cut.position.x, cut.position.y, cut.position.z);
        const quaternion = createQuaternion(cut);
        cutGroup.setRotationFromQuaternion(quaternion);
        this.group.add(cutGroup);
    }
    createArc(arc) {
        const curve = new Three.EllipseCurve(arc.xc / 1000, arc.yc / 1000, arc.rad / 1000, arc.rad / 1000, arc.fi1 * Math.PI / 180, arc.fi2 * Math.PI / 180, false, 0);
        const points = curve.getPoints(50);
        // points coming from backend are not in defined in x,y plane but rather by y and z so we need to do a switachroo
        const points3d = points.map(p => new Three.Vector3(0, p.x, p.y));
        const geometry = new Three.BufferGeometry().setFromPoints(points3d);
        return new Three.Line(geometry, this.lineMaterial);
    }
    createLine(line) {
        // points coming from backend are not in defined in x,y plane but rather by y and z so we need to do a switachroo
        const points = [
            new Three.Vector3(0, line.x1 / 1000, line.y1 / 1000),
            new Three.Vector3(0, line.x2 / 1000, line.y2 / 1000),
        ];
        const geometry = new Three.BufferGeometry().setFromPoints(points);
        return new Three.Line(geometry, this.lineMaterial);
    }
    createAnchorPoint() {
        const side = 0.008;
        let anchorGroup = new Three.Group();
        const boxGeometry = new Three.BoxGeometry(side, side, side);
        const box = new Three.Mesh(boxGeometry, this.anchorMaterial);
        const edges = new Three.EdgesGeometry(boxGeometry);
        const edgeLines = new Three.LineSegments(edges, this.lineMaterial);
        anchorGroup.add(box);
        anchorGroup.add(edgeLines);
        const pf = 1.01;
        const xyPlane = new Three.PlaneGeometry(side * pf, side * pf);
        const xyPlaneEdges = new Three.EdgesGeometry(xyPlane);
        const xzPlane = new Three.PlaneGeometry(side * pf, side * pf).rotateX(Math.PI / 2);
        const xzPlaneEdges = new Three.EdgesGeometry(xzPlane);
        const yzPlane = new Three.PlaneGeometry(side * pf, side * pf).rotateY(Math.PI / 2);
        const yzPlaneEdges = new Three.EdgesGeometry(yzPlane);
        anchorGroup.add(new Three.Mesh(xyPlane, this.anchorMaterial));
        anchorGroup.add(new Three.Mesh(xzPlane, this.anchorMaterial));
        anchorGroup.add(new Three.LineSegments(xyPlaneEdges, this.lineMaterial));
        anchorGroup.add(new Three.LineSegments(xzPlaneEdges, this.lineMaterial));
        anchorGroup.add(new Three.Mesh(yzPlane, this.anchorMaterial));
        anchorGroup.add(new Three.LineSegments(yzPlaneEdges, this.lineMaterial));
        return anchorGroup;
    }
}

class Sprites {
    constructor(viewer, group, renderer) {
        this.viewer = viewer;
        this.group = group;
        this.renderer = renderer;
        const map = new Three.TextureLoader().load('./assets/link.png');
        this.openConnectionMaterial = new Three.SpriteMaterial({ map: map });
    }
    setJsonData(jsonData) {
        this.jsonData = jsonData;
        this.updateSprites();
    }
    updateSprites() {
        disposeAndClearGroup(this.group);
        this.jsonData.sprites.forEach(spriteJson => {
            const sprite = new Three.Sprite(this.openConnectionMaterial);
            sprite.position.x = spriteJson.position.x;
            sprite.position.y = spriteJson.position.y;
            sprite.position.z = spriteJson.position.z;
            sprite.scale.set(0.05, 0.05, 1);
            this.group.add(sprite);
        });
    }
}

class ControlItem {
}
class LoadAttachment {
}
class ObjectDisplay {
}

class ControlClickEvent {
}
var ControlType;
(function (ControlType) {
    ControlType["Drive"] = "Drive";
    ControlType["Virtual"] = "Virtual";
    ControlType["None"] = "None";
})(ControlType || (ControlType = {}));
var ControlUnit;
(function (ControlUnit) {
    ControlUnit["Angle"] = "Angle";
    ControlUnit["Length"] = "Length";
})(ControlUnit || (ControlUnit = {}));
class Viewer {
    constructor(canvas, controlPositionsEmitter) {
        this.displayObjectClicked = new BehaviorSubject(new ObjectDisplay);
        this.orthographic = new Three.OrthographicCamera;
        this.perspective = new Three.PerspectiveCamera;
        this.raycaster = new Three.Raycaster();
        this.usePerspective = false;
        this.showSidePanel = false;
        this.orthoScale = 0.015;
        if (!canvas) {
            throw 'Must set canvas';
        }
        this.controlPositionsEmitter = controlPositionsEmitter;
        // update canvas height to fill div
        this.width = canvas.clientWidth > 0 ? canvas.clientWidth : canvas.width;
        this.height = canvas.clientHeight > 0 ? canvas.clientHeight : canvas.height;
        canvas.width = this.width;
        canvas.height = this.height;
        window.addEventListener('pointerdown', (event) => {
            if (event.altKey) {
                this.checkRaycastForObject(event);
            }
        });
        // window.addEventListener( 'pointermove', (event: PointerEvent) => {
        //   this.checkRaycastForComponentMasspointHover(event);
        // });
        //*******RENDERER*******
        this.renderer = new Three.WebGLRenderer({
            canvas: canvas,
            antialias: true,
            alpha: true // for transparency to work correctly
        });
        this.renderer.setClearColor(0xffffff, 0); // the color which should be transparent
        this.renderer.localClippingEnabled = true;
        this.renderer.outputColorSpace = Three.SRGBColorSpace;
        this.geometryGroup = new Three.Group();
        this.driveControlsGroup = new Three.Group();
        this.virtualControlsGroup = new Three.Group();
        this.supportGroup = new Three.Group();
        this.gridGroup = new Three.Group();
        this.cartesianGroup = new Three.Group();
        this.cylinderInfoGroup = new Three.Group();
        this.hookGroup = new Three.Group();
        this.cartesianOriginalPos = new Three.Group();
        this.workingRegionGroup = new Three.Group();
        this.hoistRopeGroup = new Three.Group();
        this.massPointGroup = new Three.Group();
        this.bearingGroup = new Three.Group();
        this.spritesGroup = new Three.Group();
        this.axleGroup = new Three.Group();
        this.componentMassGroup = new Three.Group();
        this.cutsGroup = new Three.Group();
        this.workingRegionGroup.visible = false;
        this.hoistRopeGroup.visible = true;
        this.massPointGroup.visible = true;
        this.bearingGroup.visible = true;
        this.axleGroup.visible = true;
        this.componentMassGroup.visible = false;
        this.scene = createScene();
        this.scene.add(this.geometryGroup);
        this.scene.add(this.driveControlsGroup);
        this.scene.add(this.cylinderInfoGroup);
        this.scene.add(this.virtualControlsGroup);
        this.scene.add(this.supportGroup);
        this.scene.add(this.cartesianGroup);
        this.scene.add(this.cartesianOriginalPos);
        this.scene.add(this.hookGroup);
        this.scene.add(this.workingRegionGroup);
        this.scene.add(this.hoistRopeGroup);
        this.scene.add(this.massPointGroup);
        this.scene.add(this.bearingGroup);
        this.scene.add(this.spritesGroup);
        this.scene.add(this.axleGroup);
        this.scene.add(this.componentMassGroup);
        this.scene.add(this.cutsGroup);
        this.showGrid = false;
        this.showSubCartesians = false;
        this.showWorkingRegion = false;
        this.showControls = false;
        this.showBearings = true;
        this.showAxles = true;
        this.showControlsType = ControlType.None;
        this.cartesianToggle = "original";
        this.massPointState = 0;
        this.addDefaults();
        this.machineControls = new MachineControls(this.renderer, this.getActiveCamera(), this.driveControlsGroup, this.virtualControlsGroup, this.controlPositionsEmitter, this.cylinderInfoGroup, this.geometryGroup, this.hookGroup, this.cartesianOriginalPos);
        this.workingRegion = new WorkingRegion(this, this.workingRegionGroup, this.renderer, this.getActiveCamera(), this.controlPositionsEmitter);
        this.sprites = new Sprites(this, this.spritesGroup, this.renderer);
        this.cuts = new Cuts(this, this.cutsGroup, this.renderer, this.getActiveCamera());
        this.cylinderInfoGroup.visible = true;
        window.requestAnimationFrame(this.animate.bind(this));
        this.resetCamera();
        this.renderer.dispose();
    }
    //*******ANIMATE*******
    animate() {
        //Resize items depending on Canvas size.
        //This allows for a Canvas that changes size
        this.resizeCanvasToDisplaySize();
        this.machineControls.updateButtonStates();
        // Update controls
        this.controls?.update();
        // Render the scena and camera
        if (this.usePerspective && this.perspective != null) {
            this.renderer.render(this.scene, this.perspective);
        }
        else if (this.orthographic != null) {
            this.renderer.render(this.scene, this.orthographic);
        }
        else {
            throw 'both perspective and orthographic were null';
        }
        // Initiate next frame and call tick again
        window.requestAnimationFrame(this.animate.bind(this));
    }
    /**
     * Resets camera controls before next animation frame
     */
    resetCamera() {
        this.controls?.reset();
        setCameraInitialValues(this.perspective);
        setCameraInitialValues(this.orthographic);
        this.controls?.update();
        this.machineControls?.setCamera(this.getActiveCamera());
        this.machineControls?.updateButtonStates();
    }
    swapCamera() {
        if (this.controls == null)
            throw new Error('Controls were not setup');
        if (this.usePerspective) {
            this.usePerspective = false;
            setCameraInitialValues(this.orthographic);
            this.orthographic.updateProjectionMatrix();
        }
        else {
            this.usePerspective = true;
            setCameraInitialValues(this.perspective);
            this.perspective.updateProjectionMatrix();
        }
        this.addTrackballControls();
        this.controls.update();
        this.machineControls.setCamera(this.getActiveCamera());
        createGrid(this.gridGroup, this.usePerspective);
    }
    buildMachine(jsonData) {
        disposeAndClearGroup(this.geometryGroup);
        disposeAndClearGroup(this.supportGroup);
        disposeAndClearGroup(this.bearingGroup);
        disposeAndClearGroup(this.axleGroup);
        disposeAndClearGroup(this.hoistRopeGroup);
        disposeAndClearGroup(this.massPointGroup);
        disposeAndClearGroup(this.componentMassGroup);
        if (!jsonData || !jsonData.components) {
            disposeAndClearGroup(this.driveControlsGroup);
            disposeAndClearGroup(this.virtualControlsGroup);
            disposeAndClearGroup(this.cylinderInfoGroup);
            disposeAndClearGroup(this.hookGroup);
            disposeAndClearGroup(this.cartesianOriginalPos);
            disposeAndClearGroup(this.workingRegionGroup);
            return;
        }
        // Render all geometries from datasource
        jsonData.components.forEach((item, index) => buildGeometries(this.geometryGroup, item, index, false));
        if (jsonData.supports) {
            jsonData.supports.forEach((item, index) => buildGeometries(this.supportGroup, item, index, false));
        }
        if (jsonData.bearings) {
            jsonData.bearings.forEach((item, index) => buildGeometries(this.bearingGroup, item, index, false));
        }
        jsonData.components.forEach((item, index) => buildGeometries(this.axleGroup, item, index, true));
        jsonData.hoistRopes?.forEach((hoistRope) => buildHoistRopeLine(this.hoistRopeGroup, hoistRope, jsonData.ropePathPoints));
        buildPointsGraphicForMachine(PointType.RopePathPoint, this.hoistRopeGroup, jsonData.ropePathPoints);
        buildPointsGraphicForMachine(PointType.Masspoint, this.massPointGroup, jsonData.masspoints);
        buildPointsGraphicForMachine(PointType.ComponentMasspoint, this.componentMassGroup, jsonData.componentMasspoints);
        buildCartesians(this.cartesianGroup, jsonData, this.showSubCartesians);
        this.workingRegion.setJsonData(jsonData);
        this.sprites.setJsonData(jsonData);
        this.cuts.setJsonData(jsonData);
        this.machineControls.setControls(jsonData);
        this.jsonData = jsonData;
        this.controls?.update();
        this.scene.updateMatrixWorld();
    }
    getActiveCamera() {
        if (this.usePerspective) {
            return this.perspective;
        }
        else {
            return this.orthographic;
        }
    }
    toggleGrid() {
        this.showGrid = !this.showGrid;
        if (!this.showGrid) {
            this.gridGroup.removeFromParent();
        }
        else {
            this.scene.add(this.gridGroup);
        }
    }
    toggleHoistRope() {
        this.hoistRopeGroup.visible = !this.hoistRopeGroup.visible;
    }
    toggleMassPoints() {
        this.massPointState = (this.massPointState + 1) % 3;
        switch (this.massPointState) {
            case 0: // Show the massPointGroup only.
                this.massPointGroup.visible = true;
                this.componentMassGroup.visible = false;
                break;
            case 1: // Show the componentMassGroup only.
                this.massPointGroup.visible = false;
                this.componentMassGroup.visible = true;
                break;
            case 2: // Do not show either group.
                this.massPointGroup.visible = false;
                this.componentMassGroup.visible = false;
                break;
        }
    }
    toggleBearings() {
        this.showBearings = !this.showBearings;
        this.bearingGroup.visible = this.showBearings;
    }
    toggleAxles() {
        this.showAxles = !this.showAxles;
        this.axleGroup.visible = this.showAxles;
    }
    toggleCuts() {
        this.cutsGroup.visible = !this.cutsGroup.visible;
    }
    toggleWorkingRegion() {
        this.showWorkingRegion = !this.showWorkingRegion;
        this.workingRegionGroup.visible = this.showWorkingRegion;
    }
    toggleCartesians() {
        this.cartesianGroup.visible = true;
        if (this.cartesianToggle == "none") {
            this.showSubCartesians = !this.showSubCartesians;
            buildCartesians(this.cartesianGroup, this.jsonData, this.showSubCartesians);
            this.cartesianToggle = "original";
        }
        else if (this.cartesianToggle == "original") {
            this.showSubCartesians = !this.showSubCartesians;
            buildCartesians(this.cartesianGroup, this.jsonData, this.showSubCartesians);
            this.cartesianToggle = "sub";
        }
        else {
            this.cartesianGroup.visible = false;
            this.cartesianToggle = "none";
        }
    }
    addCopyToGroupIfVisible(group, groupToAdd) {
        if (groupToAdd.visible) {
            group.add(new Three.Group().copy(groupToAdd));
        }
    }
    createGroupOfVisibleObjects() {
        let group = new Three.Group().copy(this.geometryGroup);
        this.addCopyToGroupIfVisible(group, this.geometryGroup);
        this.addCopyToGroupIfVisible(group, this.supportGroup);
        this.addCopyToGroupIfVisible(group, this.bearingGroup);
        this.addCopyToGroupIfVisible(group, this.axleGroup);
        this.addCopyToGroupIfVisible(group, this.massPointGroup);
        this.addCopyToGroupIfVisible(group, this.componentMassGroup);
        this.addCopyToGroupIfVisible(group, this.cutsGroup);
        this.addCopyToGroupIfVisible(group, this.workingRegionGroup);
        return group;
    }
    focusMachine() {
        if (!this.controls) {
            return;
        }
        this.resetCamera();
        let tempGroup = this.createGroupOfVisibleObjects();
        const machine = new Three.Box3().setFromObject(tempGroup);
        disposeAndClearGroup(tempGroup);
        const center = new Three.Vector3();
        machine.getCenter(center);
        const size = new Three.Vector3();
        machine.getSize(size);
        const maxSize = Math.max(size.x, size.y, size.z);
        const camera = this.getActiveCamera();
        const fitOffset = 1.5;
        let direction = new Three.Vector3();
        if (camera instanceof Three.PerspectiveCamera) {
            const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
            const fitWidthDistance = fitHeightDistance / camera.aspect;
            const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);
            camera.near = distance / 100;
            camera.far = distance * 100;
            direction = this.controls.target.clone()
                .sub(camera.position)
                .normalize()
                .multiplyScalar(distance);
        }
        if (camera instanceof Three.OrthographicCamera) {
            const fitHeightZoom = camera.top / (size.z / 2 * fitOffset);
            const fitWidthZoom = camera.right / (size.x / 2 * fitOffset);
            camera.zoom = Math.min(fitHeightZoom, fitWidthZoom);
            direction = this.controls.target.clone()
                .sub(camera.position)
                .normalize()
                .multiplyScalar(maxSize * fitOffset);
        }
        this.controls.target.copy(center);
        camera.updateProjectionMatrix();
        camera.position.copy(this.controls.target).sub(direction);
        this.controls.update();
    }
    isControlsEnabled() {
        return this.showControls;
    }
    emitControlValues(isControlsPositioningMethod = true) {
        this.machineControls.emitControlValues(isControlsPositioningMethod);
    }
    loadWorkingRegion(coordinates) {
        this.workingRegion.load(coordinates);
    }
    hasWorkingRegion() {
        return this.workingRegion.hasWorkingRegion();
    }
    disableControls() {
        this.showControls = false;
        this.showControlsType = ControlType.None;
        this.driveControlsGroup.visible = false;
        this.virtualControlsGroup.visible = false;
        this.cylinderInfoGroup.visible = false;
        this.cartesianOriginalPos.visible = false;
        this.workingRegionGroup.visible = false;
    }
    toggleControls() {
        if (this.showControls && this.showControlsType == ControlType.Drive) {
            this.showControls = false;
            this.showControlsType = ControlType.None;
            this.driveControlsGroup.visible = false;
            this.virtualControlsGroup.visible = false;
            this.cylinderInfoGroup.visible = false;
            this.hookGroup.visible = false;
            this.cartesianOriginalPos.visible = false;
        }
        else if (this.showControls && this.showControlsType == ControlType.Virtual) {
            this.showControlsType = ControlType.Drive;
            this.hookGroup.visible = true;
            if (!this.showSidePanel) {
                this.driveControlsGroup.visible = true;
                this.cylinderInfoGroup.visible = false;
                this.virtualControlsGroup.visible = false;
                this.cartesianOriginalPos.visible = true;
            }
        }
        else if (!this.showControls) {
            this.showControls = true;
            this.hookGroup.visible = true;
            this.showControlsType = ControlType.Virtual;
            this.cylinderInfoGroup.visible = true;
            if (!this.showSidePanel) {
                this.driveControlsGroup.visible = false;
                this.virtualControlsGroup.visible = true;
                this.cartesianOriginalPos.visible = true;
            }
        }
        this.machineControls.setCurrentToggleGroup(this.showControlsType);
        this.machineControls.updateControls();
        this.emitControlValues();
    }
    addTrackballControls() {
        if (this.controls) {
            this.controls.dispose();
        }
        this.controls = new TrackballControls(this.getActiveCamera(), this.renderer.domElement);
        this.controls.rotateSpeed = 2.0;
        this.controls.zoomSpeed = 3.0;
        this.controls.panSpeed = this.usePerspective ? 2.0 : 60;
        this.controls.staticMoving = true;
    }
    addDefaults() {
        //Cameras
        this.orthographic = new Three.OrthographicCamera(this.orthoScale * (this.width / -2), this.orthoScale * (this.width / 2), this.orthoScale * (this.height / 2), this.orthoScale * (this.height / -2), 0.1, 50);
        setCameraInitialValues(this.orthographic);
        this.orthographic.updateProjectionMatrix();
        this.perspective = new Three.PerspectiveCamera(75, this.width / this.height, 0.1, 1000);
        setCameraInitialValues(this.perspective);
        this.scene.add(this.perspective);
        this.scene.add(this.orthographic);
        //Controls
        this.addTrackballControls();
        // should probably add controls.keys here as well for keys
        //Lights
        addDefaultLights(this.scene);
        //AxisHelper
        //The X axis is red. The Y axis is green. The Z axis is blue.
        createAxisHelper(this.scene);
        //GridHelper
        createGrid(this.gridGroup, this.usePerspective);
    }
    resizeCanvasToDisplaySize() {
        const canvas = this.renderer.domElement;
        // Look up the size the canvas html element thats being displayed
        this.width = canvas.clientWidth > 0 ? canvas.clientWidth : canvas.width;
        this.height = canvas.clientHeight > 0 ? canvas.clientHeight : canvas.height;
        // Adjust displayBuffer size to match
        let currentRendererSize = new Three.Vector2();
        this.renderer.getSize(currentRendererSize);
        if (currentRendererSize.width !== this.width || currentRendererSize.height !== this.height) {
            // You must pass false here or three.js sadly fights the browser
            this.renderer.setSize(this.width, this.height, false);
            this.controls?.handleResize();
            // Update the orthographic camera
            this.orthographic.left = this.orthoScale * (this.width / -2);
            this.orthographic.right = this.orthoScale * (this.width / 2);
            this.orthographic.top = this.orthoScale * (this.height / 2);
            this.orthographic.bottom = this.orthoScale * (this.height / -2);
            this.orthographic?.updateProjectionMatrix();
        }
        //Need to recalculate this every frame or the aspect gets messed up when loading a new crane in the frontend.
        this.perspective.aspect = this.width / this.height;
        this.perspective?.updateProjectionMatrix();
    }
    getActiveControl() {
        return this.showControlsType;
    }
    hideViewControls() {
        this.driveControlsGroup.visible = false;
        this.virtualControlsGroup.visible = false;
        if (this.showControls) {
            this.cylinderInfoGroup.visible = true;
        }
        this.cartesianOriginalPos.visible = false;
    }
    showViewControls() {
        if (this.showControls && this.showControlsType == ControlType.Drive) {
            this.driveControlsGroup.visible = true;
            this.cylinderInfoGroup.visible = false;
            this.virtualControlsGroup.visible = false;
            this.hookGroup.visible = true;
            this.cartesianOriginalPos.visible = true;
        }
        else if (this.showControls && this.showControlsType == ControlType.Virtual) {
            this.driveControlsGroup.visible = false;
            this.virtualControlsGroup.visible = true;
            this.cylinderInfoGroup.visible = true;
            this.hookGroup.visible = true;
            this.cartesianOriginalPos.visible = true;
        }
    }
    showPanel() {
        this.showSidePanel = true;
        this.hideViewControls();
    }
    hidePanel() {
        this.showSidePanel = false;
        this.showViewControls();
    }
    updateControl(name, value) {
        var index = 0;
        this.jsonData.controls.forEach((control) => {
            if (control.name == name) {
                if (control.type == "Virtual") {
                    this.showControlsType = ControlType.Virtual;
                }
                else {
                    this.showControlsType = ControlType.Drive;
                }
                this.machineControls.setCurrentToggleGroup(this.showControlsType);
                this.jsonData.controls[index].value = value;
                this.emitControlValues();
            }
            index++;
        });
    }
    updateLoadAttachement(name, x, y, z) {
        if (this.jsonData.hook.name == name) {
            this.machineControls.setCurrentToggleGroup(this.showControlsType);
            this.jsonData.hook.position.x = x;
            this.jsonData.hook.position.y = y;
            this.jsonData.hook.position.z = z;
            this.emitControlValues(false);
        }
    }
    checkRaycastForObject(event) {
        const { x, y } = this.getNormalizedCoordinates(event);
        this.raycaster.setFromCamera(new Three.Vector2(x, y), this.getActiveCamera());
        const intersects = this.getIntersectedObjects();
        if (intersects.length) {
            const closestIntersect = intersects[0];
            const object = closestIntersect.object;
            if (object.parent) {
                let name = object.parent.userData.hasOwnProperty('componentName') ?
                    `${object.parent.userData['componentName']} ${object.parent.name.split('_').splice(-1)[0]}` :
                    object.parent.name;
                if (object.parent.userData['type'] === 'cut') {
                    this.displayObjectClicked.next({
                        name: name,
                        mass: undefined,
                        x: object.parent.position.x,
                        y: object.parent.position.y,
                        z: object.parent.position.z,
                    });
                }
                else {
                    this.displayObjectClicked.next({
                        name: name,
                        mass: this.getMassPointFromIntersect(closestIntersect)?.mass,
                        x: object.position.x,
                        y: object.position.y,
                        z: object.position.z,
                    });
                }
            }
        }
    }
    getNormalizedCoordinates(event) {
        const canvas = this.renderer.domElement;
        const rect = canvas.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / (rect.right - rect.left)) * 2 - 1;
        const y = ((event.clientY - rect.top) / (rect.bottom - rect.top)) * -2 + 1;
        return { x, y };
    }
    getIntersectedObjects() {
        // Gather all objects of interest before intersecting
        let objects = [];
        if (this.componentMassGroup.visible) {
            objects.push(...this.componentMassGroup.children);
        }
        if (this.massPointGroup.visible) {
            objects.push(...this.massPointGroup.children);
        }
        if (this.cutsGroup.visible) {
            objects.push(...this.cutsGroup.children.flatMap(cut => cut.children).filter(obj => obj.type === "Mesh"));
        }
        return this.raycaster.intersectObjects(objects, true);
    }
    getMassPointsGroup() {
        if (this.componentMassGroup.visible) {
            return this.jsonData.componentMasspoints;
        }
        else if (this.massPointGroup.visible) {
            return this.jsonData.masspoints;
        }
        return null;
    }
    getMassPointFromIntersect(intersect) {
        const massPoints = this.getMassPointsGroup();
        if (!massPoints) {
            return undefined;
        }
        // Get all points with matching name
        const possibleMassPoints = massPoints.filter((m) => m.name === intersect.object.parent?.name);
        if (possibleMassPoints.length === 0) {
            return undefined;
        }
        // Sort possible mass points by closest distance to intersect point
        possibleMassPoints.sort((a, b) => {
            let aDistance = intersect.point.distanceTo(new Three.Vector3(a.position.x, a.position.y, a.position.z));
            let bDistance = intersect.point.distanceTo(new Three.Vector3(b.position.x, b.position.y, b.position.z));
            return aDistance - bDistance;
        });
        return possibleMassPoints[0];
    }
}

// This class only exists to make visualization component testable
class ViewerProvider {
    createViewer(canvas, controlPositonsEmitter) {
        return new Viewer(canvas, controlPositonsEmitter);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ViewerProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ViewerProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ViewerProvider, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ProboscisViewerComponent {
    constructor(viewerProvider) {
        this.isFirstKey = true;
        this.isControlsEnabled = new BehaviorSubject(false);
        this.isLoadCaseEnabled = new BehaviorSubject(false);
        this.isOptimalPositionEnabled = new BehaviorSubject(false);
        this.isWorkingRegionEnabled = new BehaviorSubject(false);
        this.hasWorkingRegion = new BehaviorSubject(false);
        this.controlList = [];
        this.loadAttachment = { grossLoad: "0", x: 0, y: 0, z: 0, name: "", xDisplayValue: "", yDisplayValue: "", zDisplayValue: "" };
        this.isModalVisible = false;
        this.modalPosition = { x: 0, y: 0 };
        this.mousePosition = { x: 0, y: 0 };
        this.timerStart = Date.now();
        this.controlPositions = new EventEmitter();
        this.loadCase = new EventEmitter();
        this.positions$ = combineLatest([
            this.isControlsEnabled,
            this.isWorkingRegionEnabled,
            this.isOptimalPositionEnabled
        ]).pipe(map(([isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled]) => {
            return {
                toggleMenuPosition: this.getBasePosition(100, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleGridPosition: this.getBasePosition(140, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleCartesianPosition: this.getBasePosition(180, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleMassPointsPosition: this.getBasePosition(220, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleBearingsPosition: this.getBasePosition(260, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleAxlesPosition: this.getBasePosition(300, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleCutsPosition: this.getBasePosition(340, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
                toggleHoistRopePosition: this.getBasePosition(380, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled),
            };
        }));
        this.viewerProvider = viewerProvider;
        this.showPanel = false;
        this.iconMenuExpanded = false;
    }
    async ngOnInit() {
        this.viewer = this.viewerProvider.createViewer(this.canvas.nativeElement, this.controlPositions);
        this.isControlsEnabledSubscription = this.isControlsEnabled.subscribe(value => {
            if (value && !this.viewer?.isControlsEnabled()) {
                this.viewer?.toggleControls();
            }
            else if (!value && this.viewer?.isControlsEnabled()) {
                this.viewer?.toggleControls();
            }
        });
        this.displaySubscription = this.viewer.displayObjectClicked.subscribe(value => {
            if (value.name != null) {
                this.isModalVisible = false;
                this.currentModalDisplay = value;
                this.updateModalPosition();
                //Needs a short timeout or else the modal will close instantly due to click event that checks to close it.
                setTimeout(() => {
                    this.showModal();
                }, 150);
            }
            else {
                this.isModalVisible = false;
            }
        });
        window.addEventListener('pointermove', (event) => {
            this.updatePosition(event);
        });
    }
    ngOnDestroy() {
        this.isControlsEnabledSubscription?.unsubscribe();
        this.displaySubscription?.unsubscribe();
    }
    visualizeMachine(jsonData) {
        this.viewer?.buildMachine(jsonData);
        this.isModalVisible = false;
        if (this.activeControl != this.viewer?.getActiveControl() || this.viewer?.showControls == false || this.hasNewControls()) {
            this.controlList = [];
            this.viewer?.jsonData.controls.forEach((control) => {
                if (this.viewer?.getActiveControl() == control.type && this.viewer?.showControls) {
                    let unitSymblol = " mm";
                    if (control.unit == "Angle") {
                        unitSymblol = "°";
                    }
                    this.controlList.push({ value: control.value, name: control.name, unit: control.unit, displayValue: Number(control.value).toFixed(0) + unitSymblol });
                }
            });
        }
        else {
            this.viewer?.jsonData.controls.forEach((control) => {
                if (this.viewer?.getActiveControl() == control.type && this.viewer?.showControls) {
                    this.controlList.forEach(oldControl => {
                        if (oldControl.name == control.name) {
                            let unitSymblol = " mm";
                            if (control.unit == "Angle") {
                                unitSymblol = "°";
                            }
                            oldControl.value = control.value;
                            oldControl.displayValue = Number(control.value).toFixed(0) + unitSymblol;
                        }
                    });
                }
            });
        }
        this.activeControl = this.viewer?.getActiveControl();
        if (this.viewer?.jsonData.hook != null && this.viewer?.showControls) {
            this.loadAttachment.grossLoad = this.viewer?.jsonData.hook.grossLoad.toFixed(2);
            this.loadAttachment.x = this.viewer?.jsonData.hook.position.x * 1000;
            this.loadAttachment.y = this.viewer?.jsonData.hook.position.y * 1000;
            this.loadAttachment.z = this.viewer?.jsonData.hook.position.z * 1000;
            this.loadAttachment.name = this.viewer?.jsonData.hook.name;
            this.loadAttachment.xDisplayValue = (this.viewer?.jsonData.hook.position.x * 1000).toFixed(0) + " mm";
            this.loadAttachment.yDisplayValue = (this.viewer?.jsonData.hook.position.y * 1000).toFixed(0) + " mm";
            this.loadAttachment.zDisplayValue = (this.viewer?.jsonData.hook.position.z * 1000).toFixed(0) + " mm";
        }
    }
    hasNewControls() {
        const activeControlType = this.viewer?.getActiveControl();
        let newControlsCount = 0;
        const hasNewControl = this.viewer?.jsonData?.controls.some((control) => {
            const isControlTypeActive = control.type === activeControlType;
            const hasDifferentName = this.controlList.every((item) => item.name !== control.name);
            if (isControlTypeActive) {
                newControlsCount++;
                if (hasDifferentName) {
                    return true;
                }
            }
            return false;
        });
        return hasNewControl || newControlsCount !== this.controlList.length;
    }
    showControls() {
        if (this.viewer?.showControls != undefined) {
            return this.viewer?.showControls;
        }
        return false;
    }
    showLoadAttachment() {
        if (this.loadAttachment.name != "" && this.showControls()) {
            return true;
        }
        return false;
    }
    FormatUnitValue(event, control) {
        event.target.value = control.item.value.toFixed(4);
    }
    FormatUnitDisplayValue(event, control) {
        event.target.value = control.item.displayValue;
    }
    FormatLoadAttatchmentValue(event, coordinate) {
        switch (coordinate.toLowerCase()) {
            case "x":
                event.target.value = this.loadAttachment.x.toFixed(4);
                break;
            case "y":
                event.target.value = this.loadAttachment.y.toFixed(4);
                break;
            case "z":
                event.target.value = this.loadAttachment.z.toFixed(4);
                break;
            default:
                break;
        }
    }
    startIntervalPlus(event, control) {
        this.interval = setInterval(() => {
            var add = 1;
            if (event.shiftKey == true) {
                if (control.item.unit == "Length") {
                    add = 1000;
                }
                else {
                    add = 10;
                }
                this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
            }
            else {
                if (control.item.unit == "Length") {
                    add = 100;
                }
                this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
            }
        }, 100);
    }
    startIntervalMinus(event, control) {
        this.interval = setInterval(() => {
            var subtract = 1;
            if (event.shiftKey == true) {
                if (control.item.unit == "Length") {
                    subtract = 1000;
                }
                else {
                    subtract = 10;
                }
                this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - subtract);
            }
            else {
                if (control.item.unit == "Length") {
                    subtract = 100;
                }
                this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - subtract);
            }
        }, 100);
    }
    stopInterval() {
        clearInterval(this.interval);
    }
    FormatLoadAttatchmentDisplayValue(event, coordinate) {
        switch (coordinate.toLowerCase()) {
            case "x":
                event.target.value = this.loadAttachment.xDisplayValue;
                break;
            case "y":
                event.target.value = this.loadAttachment.yDisplayValue;
                break;
            case "z":
                event.target.value = this.loadAttachment.zDisplayValue;
                break;
            default:
                break;
        }
    }
    SidePanelClick() {
        this.showPanel = !this.showPanel;
        if (this.showPanel) {
            this.viewer?.showPanel();
        }
        else {
            this.viewer?.hidePanel();
        }
    }
    numberOnly(event) {
        if (event.key == 1 || event.key == 2 || event.key == 3 || event.key == 4 || event.key == 5 || event.key == 6 || event.key == 7 || event.key == 8 || event.key == 9 || event.key == 0 || event.key == "." || (event.key == "-" && (!event.shiftKey || !event.ctrlKey || !event.altKey))) {
            if (this.isFirstKey) {
                event.target.value = "";
            }
            this.isFirstKey = false;
            return true;
        }
        return false;
    }
    triggerFirstKey(event) {
        this.isFirstKey = true;
    }
    onKeyDownControl(event, control) {
        if (event.key == "Enter") {
            this.viewer?.updateControl(control.item.name.toString(), Number(event.target.value));
        }
        if (event.key == "+" && event.shiftKey) {
            var add = 10;
            if (control.item.unit == "Length") {
                add = 1000;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
        }
        if (event.key == "+" && event.altKey) {
            var add = 1;
            if (control.item.unit == "Length") {
                add = 100;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
        }
        if (event.key == "-" && event.altKey) {
            var substract = 1;
            if (control.item.unit == "Length") {
                substract = 100;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - substract);
        }
        if (event.key == "-" && event.shiftKey) {
            var subtract = 10;
            if (control.item.unit == "Length") {
                subtract = 1000;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - subtract);
        }
    }
    minusClickControl(event, control) {
        var subtract = 1;
        if (event.shiftKey == true) {
            if (control.item.unit == "Length") {
                subtract = 1000;
            }
            else {
                subtract = 10;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - subtract);
        }
        else {
            if (control.item.unit == "Length") {
                subtract = 100;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) - subtract);
        }
    }
    plusClickControl(event, control) {
        var add = 1;
        if (event.shiftKey == true) {
            if (control.item.unit == "Length") {
                add = 1000;
            }
            else {
                add = 10;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
        }
        else {
            if (control.item.unit == "Length") {
                add = 100;
            }
            this.viewer?.updateControl(control.item.name.toString(), Number(control.item.value) + add);
        }
    }
    plusClickLoadAttachment(event, coordinate) {
        var add = 100;
        if (event.shiftKey == true) {
            add = 1000;
        }
        if (coordinate == "x") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, (Number(this.loadAttachment.x) + add) / 1000, Number(this.loadAttachment.y) / 1000, Number(this.loadAttachment.z) / 1000);
        }
        if (coordinate == "y") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, (Number(this.loadAttachment.y) + add) / 1000, Number(this.loadAttachment.z) / 1000);
        }
        if (coordinate == "z") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, Number(this.loadAttachment.y) / 1000, (Number(this.loadAttachment.z) + add) / 1000);
        }
    }
    minusClickLoadAttachment(event, coordinate) {
        var subtract = 100;
        if (event.shiftKey == true) {
            subtract = 1000;
        }
        if (coordinate == "x") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, (Number(this.loadAttachment.x) - subtract) / 1000, Number(this.loadAttachment.y) / 1000, Number(this.loadAttachment.z) / 1000);
        }
        if (coordinate == "y") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, (Number(this.loadAttachment.y) - subtract) / 1000, Number(this.loadAttachment.z) / 1000);
        }
        if (coordinate == "z") {
            this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, Number(this.loadAttachment.y) / 1000, (Number(this.loadAttachment.z) - subtract) / 1000);
        }
    }
    submitLoadAttachmentValues(event, coordinate) {
        if (event.key == "Enter") {
            if (coordinate == "x") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(event.target.value) / 1000, Number(this.loadAttachment.y) / 1000, Number(this.loadAttachment.z) / 1000);
            }
            if (coordinate == "y") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, Number(event.target.value) / 1000, Number(this.loadAttachment.z) / 1000);
            }
            if (coordinate == "z") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, Number(this.loadAttachment.y) / 1000, Number(event.target.value) / 1000);
            }
        }
        if ((event.key == "+" && event.shiftKey) || (event.key == "+" && event.altKey) || (event.key == "-" && event.altKey) || (event.key == "-" && event.shiftKey)) {
            var add = 100;
            if (event.key == "+" && event.shiftKey) {
                add = 1000;
            }
            if (event.key == "-" && event.shiftKey) {
                add = -1000;
            }
            if (event.key == "-" && event.altKey) {
                add = -100;
            }
            if (coordinate == "x") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, (Number(this.loadAttachment.x) + add) / 1000, Number(this.loadAttachment.y) / 1000, Number(this.loadAttachment.z) / 1000);
            }
            if (coordinate == "y") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, (Number(this.loadAttachment.y) + add) / 1000, Number(this.loadAttachment.z) / 1000);
            }
            if (coordinate == "z") {
                this.viewer?.updateLoadAttachement(this.loadAttachment.name, Number(this.loadAttachment.x) / 1000, Number(this.loadAttachment.y) / 1000, (Number(this.loadAttachment.z) + add) / 1000);
            }
        }
    }
    /**
     * [ToggleGrid will enable/disable the grid in the viewer]
     */
    ToggleGrid() {
        this.viewer?.toggleGrid();
    }
    /**
     * [ToggleControls Will enable/disable controls depending on earlier state, will only work if EnableControls has been called first]
     */
    ToggleControls() {
        this.viewer?.toggleControls();
    }
    /**
     * [ToggleCartesians will will swap between main cartesians and subcartesians]
     */
    ToggleCartesians() {
        this.viewer?.toggleCartesians();
    }
    /**
     * [FocusMachine will refocus on the machine and center it]
     */
    FocusMachine() {
        this.viewer?.focusMachine();
    }
    OptimalPosition() {
        this.viewer?.emitControlValues(false);
    }
    ToggleWorkingRegion() {
        this.viewer?.toggleWorkingRegion();
    }
    LoadWorkingRegion(coordinates) {
        this.viewer?.loadWorkingRegion(coordinates);
        this.hasWorkingRegion.next(this.viewer?.hasWorkingRegion() ?? false);
    }
    ToggleHoistRope() {
        this.viewer?.toggleHoistRope();
    }
    ToggleMassPoints() {
        this.viewer?.toggleMassPoints();
    }
    ToggleBearings() {
        this.viewer?.toggleBearings();
    }
    ToggleAxles() {
        this.viewer?.toggleAxles();
    }
    ToggleCuts() {
        this.viewer?.toggleCuts();
    }
    /**
    * [SwapCamera will will swap between camera perspective]
    */
    SwapCamera() {
        this.viewer?.swapCamera();
    }
    /**
     * Enabled/disables controls depending on provided state param
     *
     * @param state new state for controls
     */
    SetControlsState(state) {
        this.isControlsEnabled.next(state);
        if (!state) {
            this.viewer?.disableControls();
        }
    }
    SetOptimalPositionState(state) {
        this.isOptimalPositionEnabled.next(state);
    }
    SetWorkingRegionState(state) {
        this.isWorkingRegionEnabled.next(state);
    }
    SetLoadCaseState(state) {
        this.isLoadCaseEnabled.next(state);
    }
    SaveLoadCase() {
        firstValueFrom(this.controlPositions).then((nextValue) => {
            this.loadCase.emit(nextValue);
        });
        this.viewer?.emitControlValues();
    }
    ToggleViewerButtons() {
        let menuItem = document.querySelector('.expandMenu');
        menuItem?.classList.toggle('hidden');
        this.iconMenuExpanded = !this.iconMenuExpanded;
    }
    showModal() {
        this.isModalVisible = true;
    }
    updatePosition(event) {
        this.mousePosition = { x: event.clientX, y: event.clientY };
    }
    updateModalPosition() {
        this.modalPosition = this.mousePosition;
    }
    toNumber(value) {
        return Number(value);
    }
    toMillimeter(value) {
        return Number(value * 1000).toFixed(0);
    }
    hideModal(event) {
        this.isModalVisible = false;
    }
    getBasePosition(base, isControlsEnabled, isWorkingRegionEnabled, isOptimalPositionEnabled) {
        if (isControlsEnabled) {
            base += 40;
        }
        if (isWorkingRegionEnabled) {
            base += 40;
        }
        if (isOptimalPositionEnabled) {
            base += 40;
        }
        return base;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerComponent, deps: [{ token: ViewerProvider }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: ProboscisViewerComponent, selector: "app-proboscis-viewer", outputs: { controlPositions: "controlPositions", loadCase: "loadCase" }, viewQueries: [{ propertyName: "canvas", first: true, predicate: ["canvas"], descendants: true, static: true }], ngImport: i0, template: "<div (mousedown)=\"hideModal($event)\">\n  <canvas class=\"proboscis-viewer-canvas\" #canvas></canvas>\n  <!-- Left align buttons -->\n  <div class=\"toggle swapCamera\" (click)=\"SwapCamera()\" title=\"Swap camera\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"icon\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\"\n      stroke-width=\"2\">\n      <path stroke-linecap=\"round\" stroke-linejoin=\"round\"\n        d=\"M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4\" />\n    </svg>\n  </div>\n\n  <div class=\"toggle focusMachine\" (click)=\"FocusMachine()\" title=\"Focus machine\">\n    <svg viewBox=\"0 0 18 18\" xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" stroke=\"currentColor\">\n      <g id=\"SVGRepo_bgCarrier\" stroke-width=\"0\"></g>\n      <g id=\"SVGRepo_tracerCarrier\" fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n      </g>\n      <g id=\"SVGRepo_iconCarrier\">\n        <path fill=\"none\" stroke=\"currentColor\" fill-rule=\"evenodd\"\n          d=\"M152,214 L156.508995,214 C156.780169,214 157,214.231934 157,214.5 C157,214.776142 156.77212,215 156.508995,215 L151.491005,215 C151.356934,215 151.235414,214.943306 151.146806,214.853582 C151.054763,214.760658 151,214.639137 151,214.508995 L151,209.491005 C151,209.219831 151.231934,209 151.5,209 C151.776142,209 152,209.22788 152,209.491005 L152,214 Z M168,214 L163.491005,214 C163.22788,214 163,214.223858 163,214.5 C163,214.768066 163.219831,215 163.491005,215 L168.508995,215 C168.639129,215 168.760642,214.945244 168.850231,214.856488 C168.9433,214.7646 169,214.643073 169,214.508995 L169,209.491005 C169,209.22788 168.776142,209 168.5,209 C168.231934,209 168,209.219831 168,209.491005 L168,214 Z M168.853194,197.146418 C168.764586,197.056694 168.643066,197 168.508995,197 L163.491005,197 C163.22788,197 163,197.223858 163,197.5 C163,197.768066 163.219831,198 163.491005,198 L168,198 L168,202.508995 C168,202.77212 168.223858,203 168.5,203 C168.768066,203 169,202.780169 169,202.508995 L169,197.491005 C169,197.360863 168.945237,197.239342 168.856471,197.149752 Z M151.149769,197.143512 C151.239358,197.054756 151.360871,197 151.491005,197 L156.508995,197 C156.780169,197 157,197.231934 157,197.5 C157,197.776142 156.77212,198 156.508995,198 L152,198 L152,202.508995 C152,202.780169 151.768066,203 151.5,203 C151.223858,203 151,202.77212 151,202.508995 L151,197.491005 C151,197.356927 151.0567,197.2354 151.146433,197.146791 Z M160,208 C161.104569,208 162,207.104569 162,206 C162,204.895431 161.104569,204 160,204 C158.895431,204 158,204.895431 158,206 C158,207.104569 158.895431,208 160,208 Z\"\n          transform=\"translate(-151 -197)\"></path>\n      </g>\n    </svg>\n\n  </div>\n\n  <div class=\"toggle toggleControls\" (click)=\"ToggleControls()\" title=\"Toggle controls\"\n    *ngIf=\"isControlsEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" stroke=\"currentColor\" viewBox=\"0 -960 960 960\">\n      <path\n        d=\"M427-120v-225h60v83h353v60H487v82h-60Zm-307-82v-60h247v60H120Zm187-166v-82H120v-60h187v-84h60v226h-60Zm120-82v-60h413v60H427Zm166-165v-225h60v82h187v60H653v83h-60Zm-473-83v-60h413v60H120Z\" />\n    </svg>\n  </div>\n\n  <div class=\"toggle toggleWorkingRegion\" (click)=\"ToggleWorkingRegion()\" title=\"Toggle working region\"\n    *ngIf=\"((isWorkingRegionEnabled | async) && (hasWorkingRegion | async))\">\n    <svg viewBox=\"0 0 24 24\" style=\"enable-background:new 0 0 24 24;\" xmlns=\"http://www.w3.org/2000/svg\">\n      <path\n        d=\"M 8.5 0.8 C 8 0.7 7.6 1 7.5 1.4 C 7.5 1.4 5.4 7 5.5 7.3 C 5.6 7.5 5.8 7.7 6.1 7.8 C 7.1 8.1 7.7 9 7.7 10 C 7.7 11.3 6.6 12.4 5.3 12.4 L 5.1 12.4 C 4.8 12.4 4.5 12.5 4.3 12.7 C 4.1 12.9 4 13.1 4 13.4 L 4 22.2 C 4 22.8 4.4 23.2 5 23.2 L 14.8 23.2 C 15.1 23.2 15.3 23.1 15.5 23 C 18.4 20.6 20 17 20 13.3 C 20 6.7 15.1 1.3 8.5 0.8 Z M 6 14.3 C 8 14 9.6 12.2 9.6 10 C 9.6 8.6 8.9 7.2 7.7 6.4 L 8.563 3.803 C 8.684 3.363 8.877 2.762 9.298 2.958 C 13.445 3.998 17.95 7.007 17.903 13.28 C 17.86 18.98 15.183 20.714 14.483 21.214 L 6 21.2 L 6 14.3 Z\" />\n    </svg>\n  </div>\n  <mat-spinner [diameter]=\"30\" class=\"workingRegionSpinner\" title=\"Working region is being created...\"\n    *ngIf=\"((isWorkingRegionEnabled | async) && !(hasWorkingRegion | async))\"></mat-spinner>\n  <div class=\"toggle optimalPosition\" (click)=\"OptimalPosition()\" title=\"Optimal position\"\n    *ngIf=\"isOptimalPositionEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n      <g>\n        <circle cx=\"5\" cy=\"5\" r=\"2\" />\n        <circle cx=\"12\" cy=\"5\" r=\"2\" />\n        <circle cx=\"19\" cy=\"5\" r=\"2\" />\n        <circle cx=\"5\" cy=\"12\" r=\"2\" />\n        <circle cx=\"12\" cy=\"12\" r=\"2\" />\n        <circle cx=\"19\" cy=\"12\" r=\"2\" />\n        <circle cx=\"5\" cy=\"19\" r=\"2\" />\n        <circle cx=\"12\" cy=\"19\" r=\"2\" />\n        <circle cx=\"19\" cy=\"19\" r=\"2\" />\n      </g>\n    </svg>\n  </div>\n  <div *ngIf=\"positions$ | async as positions\">\n    <div [ngStyle]=\"{'left.px': positions.toggleMenuPosition}\" class=\"toggle toggleMenu\" (click)=\"ToggleViewerButtons()\"\n      title=\"Expand menu\" *ngIf=\"(iconMenuExpanded)\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n        <path\n          d=\"M110.391 831.827v-79.218h739.218v79.218H110.391Zm0-216.218v-79.218h739.218v79.218H110.391Zm0-216.218v-79.783h739.218v79.783H110.391Z\" />\n      </svg>\n    </div>\n    <div [ngStyle]=\"{'left.px': positions.toggleMenuPosition}\" class=\"toggle toggleMenu\" (click)=\"ToggleViewerButtons()\"\n      title=\"Minimize menu\" *ngIf=\"(!iconMenuExpanded)\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n        <path\n          d=\"M97.39 831.827v-79.218H636.61v79.218H97.39Zm710.219-54.261L605.043 575l201.566-201.566L863.175 430l-145 145 146 146-56.566 56.566ZM97.39 613.609v-79.218H516.61v79.218H97.39Zm0-214.218v-79.783H636.61v79.783H97.39Z\" />\n      </svg>\n    </div>\n\n\n    <!-- Expandable buttons -->\n    <div class=\"expandMenu\">\n\n      <div [ngStyle]=\"{'left.px': positions.toggleGridPosition}\" class=\"toggle toggleGrid\" (click)=\"ToggleGrid()\"\n        title=\"Toggle grid\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" stroke=\"currentColor\" viewBox=\"0 -960 960 960\">\n          <path\n            d=\"M215-80v-135H80v-60h135v-175H80v-60h135v-175H80v-60h135v-135h60v135h175v-135h60v135h175v-135h60v135h135v60H745v175h135v60H745v175h135v60H745v135h-60v-135H510v135h-60v-135H275v135h-60Zm60-195h175v-175H275v175Zm235 0h175v-175H510v175ZM275-510h175v-175H275v175Zm235 0h175v-175H510v175Z\" />\n        </svg>\n      </div>\n\n\n      <div [ngStyle]=\"{'left.px': positions.toggleCartesianPosition}\" class=\"toggle toggleCartesians\"\n        (click)=\"ToggleCartesians()\" title=\"Toggle cartesians\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 -960 960 960\" stroke=\"currentColor\">\n          <path d=\"M200-200v-440h60v380h380v60H200Zm200-200v-440h60v380h380v60H400Z\" />\n        </svg>\n      </div>\n\n      <div [ngStyle]=\"{'left.px': positions.toggleMassPointsPosition}\" class=\"toggle toggleMasspoints\"\n        (click)=\"ToggleMassPoints()\" title=\"Toggle masspoints\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M480 976q-82 0-155-31.5t-127.5-86Q143 804 111.5 731T80 576q0-83 31.5-156t86-127Q252 239 325 207.5T480 176q83 0 156 31.5T763 293q54 54 85.5 127T880 576q0 82-31.5 155T763 858.5q-54 54.5-127 86T480 976Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleBearingsPosition}\" class=\"toggle toggleBearings\"\n        (click)=\"ToggleBearings()\" title=\"Toggle bearings\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M480 976q-83 0-156-31.5T197 859q-54-54-85.5-127T80 576q0-83 31.5-156T197 293q54-54 127-85.5T480 176q83 0 156 31.5T763 293q54 54 85.5 127T880 576q0 83-31.5 156T763 859q-54 54-127 85.5T480 976Zm0-147q106 0 179.5-73.5T733 576q0-106-73.5-179.5T480 323q-106 0-179.5 73.5T227 576q0 106 73.5 179.5T480 829Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleAxlesPosition}\" class=\"toggle toggleAxles\" (click)=\"ToggleAxles()\"\n        title=\"Toggle axles\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M180 776q-24 0-42-18t-18-42V436q0-24 18-42t42-18h600q24 0 42 18t18 42v280q0 24-18 42t-42 18H180Zm0-60h600V436H180v280Zm0 0V436v280Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleCutsPosition}\" class=\"toggle toggleCuts\" (click)=\"ToggleCuts()\"\n        title=\"Toggle cuts\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n          <path d=\"m12.60937,21.53811c-0.22197,0.1683 -0.33303,0.2524 -0.45102,0.2853c-0.10443,0.0292 -0.21229,0.0292 -0.31673,0c-0.11799,-0.0329 -0.22905,-0.117 -0.45102,-0.2853l-5.37801,-4.0762c-0.222,-0.1683 -0.333,-0.2524 -0.41373,-0.3701c-0.07143,-0.1042 -0.12539,-0.2269 -0.15837,-0.3601c-0.03729,-0.1507 -0.03729,-0.3189 -0.03729,-0.6555l0,-8.15245c0,-0.33653 0,-0.5048 0.03729,-0.65543c0.03298,-0.13328 0.08694,-0.25597 0.15837,-0.36012c0.08073,-0.1177 0.19173,-0.20183 0.41373,-0.3701l5.37801,-4.07624c0.22197,-0.16826 0.33303,-0.25239 0.45102,-0.28532c0.10443,-0.02914 0.21229,-0.02914 0.31673,0c0.11799,0.03293 0.22905,0.11706 0.45102,0.28532l5.37804,4.07624c0.22197,0.16827 0.33295,0.2524 0.4137,0.3701c0.07145,0.10415 0.12538,0.22684 0.15836,0.36012c0.03732,0.15063 0.03732,0.3189 0.03732,0.65542l0,8.15246c0,0.3366 0,0.5048 -0.03732,0.6555c-0.03298,0.1332 -0.08691,0.2559 -0.15836,0.3601c-0.08074,0.1177 -0.19173,0.2018 -0.4137,0.3701l-5.37804,4.0762z\"/>\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleHoistRopePosition}\" class=\"toggle toggleHoistRope\" (click)=\"ToggleHoistRope()\"\n        title=\"Toggle hoist rope\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n          <path d=\"M5.799995422363281,11.500004060566425 C5.799995422363281,8.461329891577947 8.350824089628219,6.000003814697266 11.499995283782482,6.000003814697266 C14.649166477936717,6.000003814697266 17.199995145201683,8.461329891577947 17.199995145201683,11.500004060566425 C17.199995145201683,14.538678229554904 14.649166477936717,17.000004306435585 11.499995283782482,17.000004306435585 C8.350824089628219,17.000004306435585 5.799995422363281,14.538678229554904 5.799995422363281,11.500004060566425 z\"/>\n          <path d=\"M10.799992700701281,5.999999976158233 L21.999998508117006,5.600001502048781 \"/>\n        </svg>\n      </div>\n    </div>\n  </div>\n\n\n\n  <!-- Buttons aligned from right side -->\n  <div class=\"toggle triggerLoadCase\" (click)=\"SaveLoadCase()\" title=\"Save single position\"\n    *ngIf=\"isLoadCaseEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fit=\"\" height=\"100%\" width=\"100%\"\n      preserveAspectRatio=\"xMidYMid meet\" focusable=\"false\">\n      <g>\n        <circle cx=\"3.8\" cy=\"12.6\" r=\"2\"></circle>\n        <circle cx=\"10.6\" cy=\"4.2\" r=\"2\"></circle>\n        <circle cx=\"14.7\" cy=\"14.3\" r=\"2\"></circle>\n        <path d=\"M14.7,21.8c-4.1,0-7.5-3.4-7.5-7.5s3.4-7.5,7.5-7.5s7.5,3.4,7.5,7.5S18.9,21.8,14.7,21.8z M14.7,8.8\n            c-3,0-5.5,2.5-5.5,5.5s2.5,5.5,5.5,5.5s5.5-2.5,5.5-5.5S17.8,8.8,14.7,8.8z\"></path>\n      </g>\n    </svg>\n  </div>\n\n  <div class=\"sidePanelToggleButton\" (click)=\"SidePanelClick()\" *ngIf=\"(!showPanel)\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n      <path\n        d=\"M110.391 831.827v-79.218h739.218v79.218H110.391Zm0-216.218v-79.218h739.218v79.218H110.391Zm0-216.218v-79.783h739.218v79.783H110.391Z\" />\n    </svg>\n  </div>\n\n  <div class=\"sidePanelToggleButton\" (click)=\"SidePanelClick()\" *ngIf=\"(showPanel)\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n      <path\n        d=\"M97.39 831.827v-79.218H636.61v79.218H97.39Zm710.219-54.261L605.043 575l201.566-201.566L863.175 430l-145 145 146 146-56.566 56.566ZM97.39 613.609v-79.218H516.61v79.218H97.39Zm0-214.218v-79.783H636.61v79.783H97.39Z\" />\n    </svg>\n  </div>\n\n  <div *ngIf=\"showPanel controlStatus\" class=\"sideControl\">\n    <tr *ngFor=\"let item of controlList\">\n      <td class=\"itemStatus\">{{item.name}}</td>\n      <input id=\"{{item.name}}\" class=\"itemStatusInput\" type=\"text\" value={{item.displayValue}}\n        (keydown)=\"onKeyDownControl($event, {item})\" (keypress)=\"numberOnly($event)\"\n        (click)=\"FormatUnitValue($event, {item})\" (focusout)=\"FormatUnitDisplayValue($event, {item})\" autocomplete=\"off\"\n        (focus)=\"triggerFirstKey($event)\">\n      <div class=\"buttonDiv\">\n        <button class=\"plusBtn\" (click)=\"plusClickControl($event, {item})\"\n          (pointerdown)=\"startIntervalPlus($event, {item})\" (pointerup)=\"stopInterval()\">+</button>\n        <button class=\"minusBtn\" (click)=\"minusClickControl($event, {item})\"\n          (pointerdown)=\"startIntervalMinus($event, {item})\" (pointerup)=\"stopInterval()\">-</button>\n      </div>\n    </tr>\n\n    <br>\n    <br>\n    <div *ngIf=\"showLoadAttachment()\">\n      <tr>\n        <td class=\"loadAttachment\">Gross Load: {{loadAttachment.grossLoad}} kg</td>\n      </tr>\n\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">X: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.xDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'x')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'x')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'x')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'x')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'x')\">-</button>\n        </div>\n      </tr>\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">Y: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.yDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'y')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'y')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'y')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'y')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'y')\">-</button>\n        </div>\n      </tr>\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">Z: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.zDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'z')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'z')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'z')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'z')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'z')\">-</button>\n        </div>\n      </tr>\n    </div>\n    <div *ngIf=\"!showControls()\">\n      <h3 class=\"controlsNotActive\">No controls are active!</h3>\n    </div>\n  </div>\n\n  <div>\n    <div class=\"info-modal\" *ngIf=\"isModalVisible\" [style.left.px]=\"modalPosition.x\" [style.top.px]=\"modalPosition.y\"\n      (click)=\"$event.stopPropagation()\">\n      <h4>{{currentModalDisplay?.name}}</h4>\n      <h4 *ngIf=\"currentModalDisplay?.mass !== undefined\">Mass: {{toNumber(currentModalDisplay?.mass) | number:'1.0-2'}} kg</h4>\n      <h4 class=\"massPointCoordinate\">X: {{toMillimeter(currentModalDisplay?.x)}} mm</h4>\n      <h4 class=\"massPointCoordinate\">Y: {{toMillimeter(currentModalDisplay?.y)}} mm</h4>\n      <h4 class=\"massPointCoordinate\">Z: {{toMillimeter(currentModalDisplay?.z)}} mm</h4>\n    </div>\n  </div>\n</div>", styles: [".proboscis-viewer-canvas{pointer-events:all;position:absolute;z-index:10;top:0;left:0;width:100%;height:100%}.toggle,.workingRegionSpinner{position:absolute;cursor:pointer;height:30px;width:30px;bottom:10px;z-index:20;fill:currentColor}.swapCamera{left:20px}.focusMachine{left:60px}.toggleControls{left:100px}.triggerLoadCase{right:20px}.toggleWorkingRegion,.workingRegionSpinner{left:140px}.optimalPosition{left:180px}.sideControl{width:fit-content}.sidePanelToggleButton{position:relative;cursor:pointer;height:30px;width:70px;z-index:20;width:30px;float:left;margin-top:5px}.controlStatus{position:absolute;cursor:pointer;height:200px;top:110px;width:100px;z-index:20}.controlsNotActive{position:relative;cursor:pointer;height:40px;width:200px;z-index:20;font-weight:700}.itemStatus{position:relative;cursor:pointer;height:40px;width:50px;z-index:20;font-weight:700;font-size:11px;color:#000;text-align:left;padding-left:3px}.loadAttachment{position:relative;cursor:pointer;height:40px;width:60px;z-index:20;font-weight:700;font-size:10px;color:#000}.loadAttachmentCoordinate{position:relative;cursor:pointer;height:40px;width:50px;z-index:20;text-align:right;font-weight:700;right:0;font-size:10px;color:#000}.itemStatusInput{position:relative;cursor:pointer;height:28px!important;width:65px;z-index:20;font-weight:700;border-radius:5px;margin:5px;font-size:11px;text-align:center}.buttonDiv{position:relative;float:right}.minusBtn{position:relative;z-index:20;display:block;width:100%;text-align:center;height:15px;line-height:2px;margin-top:4px}.plusBtn{position:relative;z-index:20;display:block;text-align:center;height:15px;line-height:2px;margin-top:4px}.hidden{display:none}.info-modal{position:fixed;display:flex;flex-direction:column;justify-content:center;align-items:left;padding:10px;border:1px solid #333;border-radius:10px;background-color:#fff;box-shadow:0 0 10px #0003;z-index:1000}.info-modal h4{margin:0 0 5px;color:#333;font-size:10px;text-align:left}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: i3.MatProgressSpinner, selector: "mat-progress-spinner, mat-spinner", inputs: ["color", "mode", "value", "diameter", "strokeWidth"], exportAs: ["matProgressSpinner"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "pipe", type: i2.DecimalPipe, name: "number" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'app-proboscis-viewer', template: "<div (mousedown)=\"hideModal($event)\">\n  <canvas class=\"proboscis-viewer-canvas\" #canvas></canvas>\n  <!-- Left align buttons -->\n  <div class=\"toggle swapCamera\" (click)=\"SwapCamera()\" title=\"Swap camera\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" class=\"icon\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\"\n      stroke-width=\"2\">\n      <path stroke-linecap=\"round\" stroke-linejoin=\"round\"\n        d=\"M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4\" />\n    </svg>\n  </div>\n\n  <div class=\"toggle focusMachine\" (click)=\"FocusMachine()\" title=\"Focus machine\">\n    <svg viewBox=\"0 0 18 18\" xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" stroke=\"currentColor\">\n      <g id=\"SVGRepo_bgCarrier\" stroke-width=\"0\"></g>\n      <g id=\"SVGRepo_tracerCarrier\" fill=\"none\" stroke=\"currentColor\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n      </g>\n      <g id=\"SVGRepo_iconCarrier\">\n        <path fill=\"none\" stroke=\"currentColor\" fill-rule=\"evenodd\"\n          d=\"M152,214 L156.508995,214 C156.780169,214 157,214.231934 157,214.5 C157,214.776142 156.77212,215 156.508995,215 L151.491005,215 C151.356934,215 151.235414,214.943306 151.146806,214.853582 C151.054763,214.760658 151,214.639137 151,214.508995 L151,209.491005 C151,209.219831 151.231934,209 151.5,209 C151.776142,209 152,209.22788 152,209.491005 L152,214 Z M168,214 L163.491005,214 C163.22788,214 163,214.223858 163,214.5 C163,214.768066 163.219831,215 163.491005,215 L168.508995,215 C168.639129,215 168.760642,214.945244 168.850231,214.856488 C168.9433,214.7646 169,214.643073 169,214.508995 L169,209.491005 C169,209.22788 168.776142,209 168.5,209 C168.231934,209 168,209.219831 168,209.491005 L168,214 Z M168.853194,197.146418 C168.764586,197.056694 168.643066,197 168.508995,197 L163.491005,197 C163.22788,197 163,197.223858 163,197.5 C163,197.768066 163.219831,198 163.491005,198 L168,198 L168,202.508995 C168,202.77212 168.223858,203 168.5,203 C168.768066,203 169,202.780169 169,202.508995 L169,197.491005 C169,197.360863 168.945237,197.239342 168.856471,197.149752 Z M151.149769,197.143512 C151.239358,197.054756 151.360871,197 151.491005,197 L156.508995,197 C156.780169,197 157,197.231934 157,197.5 C157,197.776142 156.77212,198 156.508995,198 L152,198 L152,202.508995 C152,202.780169 151.768066,203 151.5,203 C151.223858,203 151,202.77212 151,202.508995 L151,197.491005 C151,197.356927 151.0567,197.2354 151.146433,197.146791 Z M160,208 C161.104569,208 162,207.104569 162,206 C162,204.895431 161.104569,204 160,204 C158.895431,204 158,204.895431 158,206 C158,207.104569 158.895431,208 160,208 Z\"\n          transform=\"translate(-151 -197)\"></path>\n      </g>\n    </svg>\n\n  </div>\n\n  <div class=\"toggle toggleControls\" (click)=\"ToggleControls()\" title=\"Toggle controls\"\n    *ngIf=\"isControlsEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" stroke=\"currentColor\" viewBox=\"0 -960 960 960\">\n      <path\n        d=\"M427-120v-225h60v83h353v60H487v82h-60Zm-307-82v-60h247v60H120Zm187-166v-82H120v-60h187v-84h60v226h-60Zm120-82v-60h413v60H427Zm166-165v-225h60v82h187v60H653v83h-60Zm-473-83v-60h413v60H120Z\" />\n    </svg>\n  </div>\n\n  <div class=\"toggle toggleWorkingRegion\" (click)=\"ToggleWorkingRegion()\" title=\"Toggle working region\"\n    *ngIf=\"((isWorkingRegionEnabled | async) && (hasWorkingRegion | async))\">\n    <svg viewBox=\"0 0 24 24\" style=\"enable-background:new 0 0 24 24;\" xmlns=\"http://www.w3.org/2000/svg\">\n      <path\n        d=\"M 8.5 0.8 C 8 0.7 7.6 1 7.5 1.4 C 7.5 1.4 5.4 7 5.5 7.3 C 5.6 7.5 5.8 7.7 6.1 7.8 C 7.1 8.1 7.7 9 7.7 10 C 7.7 11.3 6.6 12.4 5.3 12.4 L 5.1 12.4 C 4.8 12.4 4.5 12.5 4.3 12.7 C 4.1 12.9 4 13.1 4 13.4 L 4 22.2 C 4 22.8 4.4 23.2 5 23.2 L 14.8 23.2 C 15.1 23.2 15.3 23.1 15.5 23 C 18.4 20.6 20 17 20 13.3 C 20 6.7 15.1 1.3 8.5 0.8 Z M 6 14.3 C 8 14 9.6 12.2 9.6 10 C 9.6 8.6 8.9 7.2 7.7 6.4 L 8.563 3.803 C 8.684 3.363 8.877 2.762 9.298 2.958 C 13.445 3.998 17.95 7.007 17.903 13.28 C 17.86 18.98 15.183 20.714 14.483 21.214 L 6 21.2 L 6 14.3 Z\" />\n    </svg>\n  </div>\n  <mat-spinner [diameter]=\"30\" class=\"workingRegionSpinner\" title=\"Working region is being created...\"\n    *ngIf=\"((isWorkingRegionEnabled | async) && !(hasWorkingRegion | async))\"></mat-spinner>\n  <div class=\"toggle optimalPosition\" (click)=\"OptimalPosition()\" title=\"Optimal position\"\n    *ngIf=\"isOptimalPositionEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n      <g>\n        <circle cx=\"5\" cy=\"5\" r=\"2\" />\n        <circle cx=\"12\" cy=\"5\" r=\"2\" />\n        <circle cx=\"19\" cy=\"5\" r=\"2\" />\n        <circle cx=\"5\" cy=\"12\" r=\"2\" />\n        <circle cx=\"12\" cy=\"12\" r=\"2\" />\n        <circle cx=\"19\" cy=\"12\" r=\"2\" />\n        <circle cx=\"5\" cy=\"19\" r=\"2\" />\n        <circle cx=\"12\" cy=\"19\" r=\"2\" />\n        <circle cx=\"19\" cy=\"19\" r=\"2\" />\n      </g>\n    </svg>\n  </div>\n  <div *ngIf=\"positions$ | async as positions\">\n    <div [ngStyle]=\"{'left.px': positions.toggleMenuPosition}\" class=\"toggle toggleMenu\" (click)=\"ToggleViewerButtons()\"\n      title=\"Expand menu\" *ngIf=\"(iconMenuExpanded)\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n        <path\n          d=\"M110.391 831.827v-79.218h739.218v79.218H110.391Zm0-216.218v-79.218h739.218v79.218H110.391Zm0-216.218v-79.783h739.218v79.783H110.391Z\" />\n      </svg>\n    </div>\n    <div [ngStyle]=\"{'left.px': positions.toggleMenuPosition}\" class=\"toggle toggleMenu\" (click)=\"ToggleViewerButtons()\"\n      title=\"Minimize menu\" *ngIf=\"(!iconMenuExpanded)\">\n      <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n        <path\n          d=\"M97.39 831.827v-79.218H636.61v79.218H97.39Zm710.219-54.261L605.043 575l201.566-201.566L863.175 430l-145 145 146 146-56.566 56.566ZM97.39 613.609v-79.218H516.61v79.218H97.39Zm0-214.218v-79.783H636.61v79.783H97.39Z\" />\n      </svg>\n    </div>\n\n\n    <!-- Expandable buttons -->\n    <div class=\"expandMenu\">\n\n      <div [ngStyle]=\"{'left.px': positions.toggleGridPosition}\" class=\"toggle toggleGrid\" (click)=\"ToggleGrid()\"\n        title=\"Toggle grid\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" stroke=\"currentColor\" viewBox=\"0 -960 960 960\">\n          <path\n            d=\"M215-80v-135H80v-60h135v-175H80v-60h135v-175H80v-60h135v-135h60v135h175v-135h60v135h175v-135h60v135h135v60H745v175h135v60H745v175h135v60H745v135h-60v-135H510v135h-60v-135H275v135h-60Zm60-195h175v-175H275v175Zm235 0h175v-175H510v175ZM275-510h175v-175H275v175Zm235 0h175v-175H510v175Z\" />\n        </svg>\n      </div>\n\n\n      <div [ngStyle]=\"{'left.px': positions.toggleCartesianPosition}\" class=\"toggle toggleCartesians\"\n        (click)=\"ToggleCartesians()\" title=\"Toggle cartesians\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 -960 960 960\" stroke=\"currentColor\">\n          <path d=\"M200-200v-440h60v380h380v60H200Zm200-200v-440h60v380h380v60H400Z\" />\n        </svg>\n      </div>\n\n      <div [ngStyle]=\"{'left.px': positions.toggleMassPointsPosition}\" class=\"toggle toggleMasspoints\"\n        (click)=\"ToggleMassPoints()\" title=\"Toggle masspoints\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M480 976q-82 0-155-31.5t-127.5-86Q143 804 111.5 731T80 576q0-83 31.5-156t86-127Q252 239 325 207.5T480 176q83 0 156 31.5T763 293q54 54 85.5 127T880 576q0 82-31.5 155T763 858.5q-54 54.5-127 86T480 976Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleBearingsPosition}\" class=\"toggle toggleBearings\"\n        (click)=\"ToggleBearings()\" title=\"Toggle bearings\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M480 976q-83 0-156-31.5T197 859q-54-54-85.5-127T80 576q0-83 31.5-156T197 293q54-54 127-85.5T480 176q83 0 156 31.5T763 293q54 54 85.5 127T880 576q0 83-31.5 156T763 859q-54 54-127 85.5T480 976Zm0-147q106 0 179.5-73.5T733 576q0-106-73.5-179.5T480 323q-106 0-179.5 73.5T227 576q0 106 73.5 179.5T480 829Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleAxlesPosition}\" class=\"toggle toggleAxles\" (click)=\"ToggleAxles()\"\n        title=\"Toggle axles\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n          <path\n            d=\"M180 776q-24 0-42-18t-18-42V436q0-24 18-42t42-18h600q24 0 42 18t18 42v280q0 24-18 42t-42 18H180Zm0-60h600V436H180v280Zm0 0V436v280Z\" />\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleCutsPosition}\" class=\"toggle toggleCuts\" (click)=\"ToggleCuts()\"\n        title=\"Toggle cuts\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n          <path d=\"m12.60937,21.53811c-0.22197,0.1683 -0.33303,0.2524 -0.45102,0.2853c-0.10443,0.0292 -0.21229,0.0292 -0.31673,0c-0.11799,-0.0329 -0.22905,-0.117 -0.45102,-0.2853l-5.37801,-4.0762c-0.222,-0.1683 -0.333,-0.2524 -0.41373,-0.3701c-0.07143,-0.1042 -0.12539,-0.2269 -0.15837,-0.3601c-0.03729,-0.1507 -0.03729,-0.3189 -0.03729,-0.6555l0,-8.15245c0,-0.33653 0,-0.5048 0.03729,-0.65543c0.03298,-0.13328 0.08694,-0.25597 0.15837,-0.36012c0.08073,-0.1177 0.19173,-0.20183 0.41373,-0.3701l5.37801,-4.07624c0.22197,-0.16826 0.33303,-0.25239 0.45102,-0.28532c0.10443,-0.02914 0.21229,-0.02914 0.31673,0c0.11799,0.03293 0.22905,0.11706 0.45102,0.28532l5.37804,4.07624c0.22197,0.16827 0.33295,0.2524 0.4137,0.3701c0.07145,0.10415 0.12538,0.22684 0.15836,0.36012c0.03732,0.15063 0.03732,0.3189 0.03732,0.65542l0,8.15246c0,0.3366 0,0.5048 -0.03732,0.6555c-0.03298,0.1332 -0.08691,0.2559 -0.15836,0.3601c-0.08074,0.1177 -0.19173,0.2018 -0.4137,0.3701l-5.37804,4.0762z\"/>\n        </svg>\n      </div>\n      <div [ngStyle]=\"{'left.px': positions.toggleHoistRopePosition}\" class=\"toggle toggleHoistRope\" (click)=\"ToggleHoistRope()\"\n        title=\"Toggle hoist rope\">\n        <svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\" stroke=\"currentColor\" stroke-width=\"2\">\n          <path d=\"M5.799995422363281,11.500004060566425 C5.799995422363281,8.461329891577947 8.350824089628219,6.000003814697266 11.499995283782482,6.000003814697266 C14.649166477936717,6.000003814697266 17.199995145201683,8.461329891577947 17.199995145201683,11.500004060566425 C17.199995145201683,14.538678229554904 14.649166477936717,17.000004306435585 11.499995283782482,17.000004306435585 C8.350824089628219,17.000004306435585 5.799995422363281,14.538678229554904 5.799995422363281,11.500004060566425 z\"/>\n          <path d=\"M10.799992700701281,5.999999976158233 L21.999998508117006,5.600001502048781 \"/>\n        </svg>\n      </div>\n    </div>\n  </div>\n\n\n\n  <!-- Buttons aligned from right side -->\n  <div class=\"toggle triggerLoadCase\" (click)=\"SaveLoadCase()\" title=\"Save single position\"\n    *ngIf=\"isLoadCaseEnabled | async as boolean\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fit=\"\" height=\"100%\" width=\"100%\"\n      preserveAspectRatio=\"xMidYMid meet\" focusable=\"false\">\n      <g>\n        <circle cx=\"3.8\" cy=\"12.6\" r=\"2\"></circle>\n        <circle cx=\"10.6\" cy=\"4.2\" r=\"2\"></circle>\n        <circle cx=\"14.7\" cy=\"14.3\" r=\"2\"></circle>\n        <path d=\"M14.7,21.8c-4.1,0-7.5-3.4-7.5-7.5s3.4-7.5,7.5-7.5s7.5,3.4,7.5,7.5S18.9,21.8,14.7,21.8z M14.7,8.8\n            c-3,0-5.5,2.5-5.5,5.5s2.5,5.5,5.5,5.5s5.5-2.5,5.5-5.5S17.8,8.8,14.7,8.8z\"></path>\n      </g>\n    </svg>\n  </div>\n\n  <div class=\"sidePanelToggleButton\" (click)=\"SidePanelClick()\" *ngIf=\"(!showPanel)\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n      <path\n        d=\"M110.391 831.827v-79.218h739.218v79.218H110.391Zm0-216.218v-79.218h739.218v79.218H110.391Zm0-216.218v-79.783h739.218v79.783H110.391Z\" />\n    </svg>\n  </div>\n\n  <div class=\"sidePanelToggleButton\" (click)=\"SidePanelClick()\" *ngIf=\"(showPanel)\">\n    <svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 96 960 960\" stroke=\"currentColor\">\n      <path\n        d=\"M97.39 831.827v-79.218H636.61v79.218H97.39Zm710.219-54.261L605.043 575l201.566-201.566L863.175 430l-145 145 146 146-56.566 56.566ZM97.39 613.609v-79.218H516.61v79.218H97.39Zm0-214.218v-79.783H636.61v79.783H97.39Z\" />\n    </svg>\n  </div>\n\n  <div *ngIf=\"showPanel controlStatus\" class=\"sideControl\">\n    <tr *ngFor=\"let item of controlList\">\n      <td class=\"itemStatus\">{{item.name}}</td>\n      <input id=\"{{item.name}}\" class=\"itemStatusInput\" type=\"text\" value={{item.displayValue}}\n        (keydown)=\"onKeyDownControl($event, {item})\" (keypress)=\"numberOnly($event)\"\n        (click)=\"FormatUnitValue($event, {item})\" (focusout)=\"FormatUnitDisplayValue($event, {item})\" autocomplete=\"off\"\n        (focus)=\"triggerFirstKey($event)\">\n      <div class=\"buttonDiv\">\n        <button class=\"plusBtn\" (click)=\"plusClickControl($event, {item})\"\n          (pointerdown)=\"startIntervalPlus($event, {item})\" (pointerup)=\"stopInterval()\">+</button>\n        <button class=\"minusBtn\" (click)=\"minusClickControl($event, {item})\"\n          (pointerdown)=\"startIntervalMinus($event, {item})\" (pointerup)=\"stopInterval()\">-</button>\n      </div>\n    </tr>\n\n    <br>\n    <br>\n    <div *ngIf=\"showLoadAttachment()\">\n      <tr>\n        <td class=\"loadAttachment\">Gross Load: {{loadAttachment.grossLoad}} kg</td>\n      </tr>\n\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">X: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.xDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'x')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'x')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'x')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'x')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'x')\">-</button>\n        </div>\n      </tr>\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">Y: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.yDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'y')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'y')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'y')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'y')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'y')\">-</button>\n        </div>\n      </tr>\n      <tr>\n        <td class=\"loadAttachmentCoordinate\">Z: </td>\n        <input class=\"itemStatusInput\" type=\"text\" value={{loadAttachment.zDisplayValue}}\n          (keyup)=\"submitLoadAttachmentValues($event, 'z')\" (keypress)=\"numberOnly($event)\"\n          (click)=\"FormatLoadAttatchmentValue($event, 'z')\" (focusout)=\"FormatLoadAttatchmentDisplayValue($event, 'z')\"\n          autocomplete=\"off\" (focus)=\"triggerFirstKey($event)\">\n        <div class=\"buttonDiv\">\n          <button class=\"plusBtn\" (click)=\"plusClickLoadAttachment($event, 'z')\">+</button>\n          <button class=\"minusBtn\" (click)=\"minusClickLoadAttachment($event, 'z')\">-</button>\n        </div>\n      </tr>\n    </div>\n    <div *ngIf=\"!showControls()\">\n      <h3 class=\"controlsNotActive\">No controls are active!</h3>\n    </div>\n  </div>\n\n  <div>\n    <div class=\"info-modal\" *ngIf=\"isModalVisible\" [style.left.px]=\"modalPosition.x\" [style.top.px]=\"modalPosition.y\"\n      (click)=\"$event.stopPropagation()\">\n      <h4>{{currentModalDisplay?.name}}</h4>\n      <h4 *ngIf=\"currentModalDisplay?.mass !== undefined\">Mass: {{toNumber(currentModalDisplay?.mass) | number:'1.0-2'}} kg</h4>\n      <h4 class=\"massPointCoordinate\">X: {{toMillimeter(currentModalDisplay?.x)}} mm</h4>\n      <h4 class=\"massPointCoordinate\">Y: {{toMillimeter(currentModalDisplay?.y)}} mm</h4>\n      <h4 class=\"massPointCoordinate\">Z: {{toMillimeter(currentModalDisplay?.z)}} mm</h4>\n    </div>\n  </div>\n</div>", styles: [".proboscis-viewer-canvas{pointer-events:all;position:absolute;z-index:10;top:0;left:0;width:100%;height:100%}.toggle,.workingRegionSpinner{position:absolute;cursor:pointer;height:30px;width:30px;bottom:10px;z-index:20;fill:currentColor}.swapCamera{left:20px}.focusMachine{left:60px}.toggleControls{left:100px}.triggerLoadCase{right:20px}.toggleWorkingRegion,.workingRegionSpinner{left:140px}.optimalPosition{left:180px}.sideControl{width:fit-content}.sidePanelToggleButton{position:relative;cursor:pointer;height:30px;width:70px;z-index:20;width:30px;float:left;margin-top:5px}.controlStatus{position:absolute;cursor:pointer;height:200px;top:110px;width:100px;z-index:20}.controlsNotActive{position:relative;cursor:pointer;height:40px;width:200px;z-index:20;font-weight:700}.itemStatus{position:relative;cursor:pointer;height:40px;width:50px;z-index:20;font-weight:700;font-size:11px;color:#000;text-align:left;padding-left:3px}.loadAttachment{position:relative;cursor:pointer;height:40px;width:60px;z-index:20;font-weight:700;font-size:10px;color:#000}.loadAttachmentCoordinate{position:relative;cursor:pointer;height:40px;width:50px;z-index:20;text-align:right;font-weight:700;right:0;font-size:10px;color:#000}.itemStatusInput{position:relative;cursor:pointer;height:28px!important;width:65px;z-index:20;font-weight:700;border-radius:5px;margin:5px;font-size:11px;text-align:center}.buttonDiv{position:relative;float:right}.minusBtn{position:relative;z-index:20;display:block;width:100%;text-align:center;height:15px;line-height:2px;margin-top:4px}.plusBtn{position:relative;z-index:20;display:block;text-align:center;height:15px;line-height:2px;margin-top:4px}.hidden{display:none}.info-modal{position:fixed;display:flex;flex-direction:column;justify-content:center;align-items:left;padding:10px;border:1px solid #333;border-radius:10px;background-color:#fff;box-shadow:0 0 10px #0003;z-index:1000}.info-modal h4{margin:0 0 5px;color:#333;font-size:10px;text-align:left}\n"] }]
        }], ctorParameters: () => [{ type: ViewerProvider }], propDecorators: { canvas: [{
                type: ViewChild,
                args: ['canvas', { static: true }]
            }], controlPositions: [{
                type: Output
            }], loadCase: [{
                type: Output
            }] } });

class ProboscisViewerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerModule, declarations: [ProboscisViewerComponent], imports: [CommonModule,
            MatProgressSpinnerModule], exports: [ProboscisViewerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerModule, imports: [CommonModule,
            MatProgressSpinnerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ProboscisViewerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ProboscisViewerComponent
                    ],
                    imports: [
                        CommonModule,
                        MatProgressSpinnerModule,
                    ],
                    exports: [
                        ProboscisViewerComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of proboscis-viewer
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BearingType, CalculationStatus, ControlItem, LoadAttachment, ObjectDisplay, PointType, ProboscisViewerComponent, ProboscisViewerModule, ProboscisViewerService, Unit, ViewerProvider };
